import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import Plot from "react-plotly.js";
import {
  fetchGlobalView,
  fetchGlobalConstraints,
  fetchGlobalForecast,
  fetchGlobalWinrate,
  fetchFamilyDetailsByMode,
  downloadFamilyList,
  fetchSkuList,
  fetchSimilarSkus,
  downloadSkuList,
  fetchRunList,
  downloadGlobalForecast,
  downloadGlobalWinrate,
  downloadAllSkusForecast,
  downloadAllSkusWinrate,
  fetchConfig,
} from "./api";
import {
  getKpiChartConfig,
  getPieChartConfig,
  getForecastChartConfig,
  getWinrateChartConfig,
  aggregateByWeek,
  aggregateWinrateByWeek,
  formatPeriod,
} from "./chartData";
import { useAlertsCount, useAlertsLoading } from "./AlertsContext";
import InfoIcon, { SortableHeader } from "./InfoIcon";
import { useAuth } from "./AuthContext";
import ChangePasswordModal from "./ChangePasswordModal";
import "./App.css";

const iconVisible = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    fill="#f5f5f5"
    viewBox="0 0 256 256"
  >
    <path d="M247.31,124.76c-.35-.79-8.82-19.58-27.65-38.41C194.57,61.26,162.88,48,128,48S61.43,61.26,36.34,86.35C17.51,105.18,9,124,8.69,124.76a8,8,0,0,0,0,6.5c.35.79,8.82,19.57,27.65,38.4C61.43,194.74,93.12,208,128,208s66.57-13.26,91.66-38.34c18.83-18.83,27.3-37.61,27.65-38.4A8,8,0,0,0,247.31,124.76ZM128,168a40,40,0,1,1,40-40A40,40,0,0,1,128,168Z"></path>
  </svg>
);

const iconHidden = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    fill="#f5f5f5"
    viewBox="0 0 256 256"
  >
    <path d="M96.68,57.87a4,4,0,0,1,2.08-6.6A130.13,130.13,0,0,1,128,48c34.88,0,66.57,13.26,91.66,38.35,18.83,18.83,27.3,37.62,27.65,38.41a8,8,0,0,1,0,6.5c-.35.79-8.82,19.57-27.65,38.4q-4.28,4.26-8.79,8.07a4,4,0,0,1-5.55-.36ZM213.92,210.62a8,8,0,1,1-11.84,10.76L180,197.13A127.21,127.21,0,0,1,128,208c-34.88,0-66.57-13.26-91.66-38.34C17.51,150.83,9,132.05,8.69,131.26a8,8,0,0,1,0-6.5C9,124,17.51,105.18,36.34,86.35a135,135,0,0,1,25-19.78L42.08,45.38A8,8,0,1,1,53.92,34.62Zm-65.49-48.25-52.69-58a40,40,0,0,0,52.69,58Z"></path>
  </svg>
);

const iconPlus = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    fill="#ffffff"
    viewBox="0 0 256 256"
  >
    <path d="M228,128a12,12,0,0,1-12,12H140v76a12,12,0,0,1-24,0V140H40a12,12,0,0,1,0-24h76V40a12,12,0,0,1,24,0v76h76A12,12,0,0,1,228,128Z"></path>
  </svg>
);

const iconMinus = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    fill="#ffffff"
    viewBox="0 0 256 256"
  >
    <path d="M228,128a12,12,0,0,1-12,12H40a12,12,0,0,1,0-24H216A12,12,0,0,1,228,128Z"></path>
  </svg>
);

const iconDownload = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="#ffffff"
    viewBox="0 0 256 256"
  >
    <path d="M222,144v64a6,6,0,0,1-6,6H40a6,6,0,0,1-6-6V144a6,6,0,0,1,12,0v58H210V144a6,6,0,0,1,12,0Zm-98.24,4.24a6,6,0,0,0,8.48,0l40-40a6,6,0,0,0-8.48-8.48L134,129.51V32a6,6,0,0,0-12,0v97.51L92.24,99.76a6,6,0,0,0-8.48,8.48Z"></path>
  </svg>
);

// Icone freccia rimosse (Drag & Drop replacement)

const constraintLabelMap = {
  maxPercUnallSku: "Maximum Perc Unallocated Sku",
};

// Configurazione delle colonne per il reordering delle colonne
const FAMILY_COLUMN_DEFS = {
  ROI: {
    sortKey: "roi",
    render: (row, formatPercentage) => formatPercentage(row.roi),
    className: (row) =>
      row.min_roi == null
        ? "roi-value"
        : row.roi >= row.min_roi
          ? "roi-value above-min"
          : "roi-value below-min",
  },
  "MINIMUM ROI": {
    sortKey: "min_roi",
    render: (row, formatPercentage) => formatPercentage(row.min_roi),
  },
  "SERVICE LEVEL": {
    sortKey: "service_level",
    render: (row, formatPercentage) => formatPercentage(row.service_level),
  },
  "GROSS MARGIN": {
    sortKey: "gross_margin",
    render: (row, _, formatCurrency) => formatCurrency(row.gross_margin),
  },
  "NET MARGIN": {
    sortKey: "net_margin",
    render: (row, _, formatCurrency) => formatCurrency(row.net_margin),
  },
  "TOTAL COST": {
    sortKey: "budget",
    render: (row, _, formatCurrency) => formatCurrency(row.budget),
    className: (row) =>
      row.max_budget != null && row.budget <= row.max_budget
        ? "positive-value"
        : "negative-value",
  },
  "INVENTORY COST": {
    sortKey: "cost",
    render: (row, _, formatCurrency) => formatCurrency(row.cost),
  },
  "SHELF STOCKOUT RATE": {
    sortKey: "shelf_stockout_rate",
    render: (row, formatPercentage) =>
      formatPercentage(row.shelf_stockout_rate),
  },
  REVENUE: {
    sortKey: "revenue",
    render: (row, _, formatCurrency) => formatCurrency(row.revenue),
  },
  COGS: {
    sortKey: "cogs",
    render: (row, _, formatCurrency) => formatCurrency(row.cogs),
  },
  "PNA AVAILABILITY": {
    sortKey: "pna_availability",
    render: (row, formatPercentage) => formatPercentage(row.pna_availability),
  },
};

const SKU_COLUMN_DEFS = {
  "SKU family": {
    sortKey: "family",
    render: (row) => row.family,
  },
  ROI: {
    sortKey: "roi",
    render: (row, formatPercentage) => formatPercentage(row.roi),
    className: (row) =>
      row.min_roi == null
        ? "roi-value"
        : row.roi >= row.min_roi
          ? "roi-value above-min"
          : "roi-value below-min",
  },
  "MINIMUM ROI": {
    sortKey: "min_roi",
    render: (row, formatPercentage) => formatPercentage(row.min_roi),
  },
  LQ: {
    sortKey: "lq",
    render: (row, _, __, formatNumber) => formatNumber(row.lq),
  },
  ROQ: {
    sortKey: "rq",
    render: (row, _, __, formatNumber) => formatNumber(row.rq),
  },
  "SERVICE LEVEL": {
    sortKey: "service_level",
    render: (row, formatPercentage) => formatPercentage(row.service_level),
  },
  "INVENTORY COST": {
    sortKey: "cost",
    render: (row, _, formatCurrency) => formatCurrency(row.cost),
  },
  "GROSS MARGIN": {
    sortKey: "gross_margin",
    render: (row, _, formatCurrency) => formatCurrency(row.gross_margin),
  },
  "NET MARGIN": {
    sortKey: "net_margin",
    render: (row, _, formatCurrency) => formatCurrency(row.net_margin),
  },
  "SHELF STOCKOUT RATE": {
    sortKey: "shelf_stockout_rate",
    render: (row, formatPercentage) =>
      formatPercentage(row.shelf_stockout_rate),
  },
  "UNIT COST": {
    sortKey: "unit_cost",
    render: (row, _, __, ___, formatUnitCurrency) =>
      formatUnitCurrency(row.unit_cost),
  },
  "UNIT PRICE": {
    sortKey: "unit_price",
    render: (row, _, __, ___, formatUnitCurrency) =>
      formatUnitCurrency(row.unit_price),
  },
  "TOTAL COST": {
    sortKey: "budget",
    render: (row, _, formatCurrency) => formatCurrency(row.budget),
  },
  REVENUE: {
    sortKey: "revenue",
    render: (row, _, formatCurrency) => formatCurrency(row.revenue),
  },
  COGS: {
    sortKey: "cogs",
    render: (row, _, formatCurrency) => formatCurrency(row.cogs),
  },
  "PNA AVAILABILITY": {
    sortKey: "pna_availability",
    render: (row, formatPercentage) => formatPercentage(row.pna_availability),
  },
  "INBOUND COSTS": {
    sortKey: "inbound_costs",
    render: (row, _, formatCurrency) => formatCurrency(row.inbound_costs),
  },
  "OUTBOUND COSTS": {
    sortKey: "outbound_costs",
    render: (row, _, formatCurrency) => formatCurrency(row.outbound_costs),
  },
  "CARRY COSTS": {
    sortKey: "carry_costs",
    render: (row, _, formatCurrency) => formatCurrency(row.carry_costs),
  },
  "MARGIN ADJUSTERS": {
    sortKey: "margin_adjusters",
    render: (row, _, formatCurrency) => formatCurrency(row.margin_adjusters),
  },
  "ON HAND AVG QTY": {
    sortKey: "on_hand_mean",
    render: (row, _, __, formatNumber) => formatNumber(row.on_hand_mean),
  },
  "ORDERS PER YEAR": {
    sortKey: "orders_per_year",
    render: (row, _, __, formatNumber) => formatNumber(row.orders_per_year),
  },
  "FC CLASS": {
    sortKey: "fc_classification",
    render: (row) => row.fc_classification || "N/A",
  },
  "TARGET FILL RATE": {
    sortKey: "target_fill_rate",
    render: (row, formatPercentage) => formatPercentage(row.target_fill_rate),
  },
  "FCST IN LT": {
    sortKey: "fcst_in_lt",
    render: (row, _, __, formatNumber) => formatNumber(row.fcst_in_lt),
  },
  "SAFETY STOCK": {
    sortKey: "safety_stock",
    render: (row, _, __, formatNumber) => formatNumber(row.safety_stock),
  },
  "LEAD TIME IN WEEKS": {
    sortKey: "weekly_lead_time",
    render: (row, _, __, formatNumber) => formatNumber(row.weekly_lead_time),
  },
};

const forecastColumns = [
  "MONTH",
  "ACT QTY Y-1",
  "FCST QTY - σ",
  "FCST QTY (avg)",
  "FCST QTY + σ",
  "ACT VAL Y-1",
  "FCST VALUE - σ",
  "FCST VALUE (avg)",
  "FCST VALUE + σ",
];

const optionalSkuColumns = [
  "SKU family",
  "FC CLASS",
  "ROI",
  "MINIMUM ROI",
  "LQ",
  "ROQ",
  "SAFETY STOCK",
  "ON HAND AVG QTY",
  "SERVICE LEVEL",
  "TARGET FILL RATE",
  "INVENTORY COST",
  "TOTAL COST",
  "REVENUE",
  "COGS",
  "GROSS MARGIN",
  "NET MARGIN",
  "SHELF STOCKOUT RATE",
  "UNIT COST",
  "UNIT PRICE",
  "PNA AVAILABILITY",
  "INBOUND COSTS",
  "OUTBOUND COSTS",
  "CARRY COSTS",
  "MARGIN ADJUSTERS",
  "ORDERS PER YEAR",
  "FCST IN LT",
  "LEAD TIME IN WEEKS",
];

const optionalFamilyColumns = [
  "ROI",
  "MINIMUM ROI",
  "SERVICE LEVEL",
  "GROSS MARGIN",
  "NET MARGIN",
  "TOTAL COST",
  "REVENUE",
  "COGS",
  "INVENTORY COST",
  "SHELF STOCKOUT RATE",
  "PNA AVAILABILITY",
];

const familyConfigMap = {
  roi: "ROI",
  minimum_roi: "MINIMUM ROI",
  sl: "SERVICE LEVEL",
  gross_margin: "GROSS MARGIN",
  net_margin: "NET MARGIN",
  budget: "TOTAL COST",
  revenue: "REVENUE",
  cogs: "COGS",
  cost: "INVENTORY COST",
  shelf_stockout_rate: "SHELF STOCKOUT RATE",
  pna_availability: "PNA AVAILABILITY",
};

const skuConfigMap = {
  sku_family: "SKU family",
  roi: "ROI",
  lq: "LQ",
  roq: "ROQ",
  sl: "SERVICE LEVEL",
  gross_margin: "GROSS MARGIN",
  net_margin: "NET MARGIN",
  cost: "INVENTORY COST",
  budget: "TOTAL COST",
  revenue: "REVENUE",
  cogs: "COGS",
  min_roi: "MINIMUM ROI",
  shelf_stockout_rate: "SHELF STOCKOUT RATE",
  unit_cost: "UNIT COST",
  unit_price: "UNIT PRICE",
  pna_availability: "PNA AVAILABILITY",
  inbound_costs: "INBOUND COSTS",
  outbound_costs: "OUTBOUND COSTS",
  carry_costs: "CARRY COSTS",
  margin_adjusters: "MARGIN ADJUSTERS",
  on_hand_mean: "ON HAND AVG QTY",
  orders_per_year: "ORDERS PER YEAR",
  fc_classification: "FC CLASS",
  target_fill_rate: "TARGET FILL RATE",
  fcst_in_lt: "FCST IN LT",
  safety_stock: "SAFETY STOCK",
  weekly_lead_time: "LEAD TIME IN WEEKS",
};

const forecastMap = {
  MONTH: "year_week",
  "ACT QTY Y-1": "actual_qty_last_year",
  "FCST QTY - σ": "fcst_qty_lowerbound",
  "FCST QTY (avg)": "fcst_qty_avg",
  "FCST QTY + σ": "fcst_qty_upperbound",
  "ACT VAL Y-1": "actual_val_last_year",
  "FCST VALUE - σ": "fcst_val_lowerbound",
  "FCST VALUE (avg)": "fcst_val_avg",
  "FCST VALUE + σ": "fcst_val_upperbound",
};

const winrateColumns = [
  "MONTH",
  "ACT WR Y-1 RAW",
  "FCST WR (avg) RAW",
  "ACT WR Y-1 SHRINKED",
  "FCST WR (avg) SHRINKED",
];

const winrateMap = {
  MONTH: "year_week",
  "ACT WR Y-1 RAW": "actual_wr_last_year",
  "FCST WR (avg) RAW": "fcst_qty_avg",
  "ACT WR Y-1 SHRINKED": "actual_wr_last_year_shrinked",
  "FCST WR (avg) SHRINKED": "fcst_qty_avg_shrinked",
};

// Questo hook salva/carica automaticamente dal localStorage
function useStickyState(defaultValue, key) {
  const [value, setValue] = useState(() => {
    try {
      const stickyValue = window.localStorage.getItem(key);
      return stickyValue !== null ? JSON.parse(stickyValue) : defaultValue;
    } catch (e) {
      console.warn("Error reading from localStorage", e);
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.warn("Error writing to localStorage", e);
    }
  }, [key, value]);

  return [value, setValue];
}

function App() {
  const alertsCount = useAlertsCount();
  const alertsLoading = useAlertsLoading();
  const { logout } = useAuth();
  const navigate   = useNavigate();

  const [config, setConfig] = useState(null);
  const [globalView, setGlobalView] = useState(null);
  const [kpiHistory, setKpiHistory] = useState(null);
  const [unconstrainedKpiHistory, setUnconstrainedKpiHistory] = useState(null);
  const [globalConstraints, setGlobalConstraints] = useState(null);
  const [forecastData, setForecastData] = useState([]);
  const [allForecastData, setAllForecastData] = useState([]);
  const [availableRuns, setAvailableRuns] = useState([]);
  const [selectedGenerationIndex, setSelectedGenerationIndex] = useState(0);
  const [forecastMode, setForecastMode] = useState("demand");
  const [customerType, setCustomerType] = useState("combined");
  const [availabilityType, setAvailabilityType] = useState("available");
  const [winrateData, setWinrateData] = useState([]);
  const [allWinrateData, setAllWinrateData] = useState([]);
  const [winrateRuns, setWinrateRuns] = useState([]);
  const [selectedWinrateGenIndex, setSelectedWinrateGenIndex] = useState(0);
  const [familyTableData, setFamilyTableData] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const [skuTableData, setSkuTableData] = useState([]);
  const [currentFamilyRunId, setCurrentFamilyRunId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloadingFamily, setIsDownloadingFamily] = useState(false);
  const [isDownloadingSku, setIsDownloadingSku] = useState(false);
  const [isDownloadingForecast, setIsDownloadingForecast] = useState(false);
  const [isDownloadingAllSkus, setIsDownloadingAllSkus] = useState(false);
  const [isDownloadDropdownOpen, setIsDownloadDropdownOpen] = useState(false);
  const [isFamilyDownloadDropdownOpen, setIsFamilyDownloadDropdownOpen] =
    useState(false);
  const [isSkuDownloadDropdownOpen, setIsSkuDownloadDropdownOpen] =
    useState(false);
  const [isDownloadingWinrate, setIsDownloadingWinrate] = useState(false);
  const [isDownloadingAllSkusWinrate, setIsDownloadingAllSkusWinrate] = useState(false);
  const [isWinrateDropdownOpen, setIsWinrateDropdownOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const userMenuRef = useRef(null);
  const familyColsRef = useRef(null);
  const skuColsRef = useRef(null);
  const forecastColsRef = useRef(null);
  const [familyFilterInputs, setFamilyFilterInputs] = useState([
    {
      id: Date.now(),
      column: "roi",
      operator: ">",
      value: "",
    },
  ]);
  const [skuFilterInputs, setSkuFilterInputs] = useState([
    {
      id: Date.now(),
      column: "roi",
      operator: ">",
      value: "",
    },
  ]);
  const [appliedFamilyFilters, setAppliedFamilyFilters] = useState({});
  const [appliedSkuFilters, setAppliedSkuFilters] = useState({});
  const [popupChart, setPopupChart] = useState(null);
  const [skuCurrentPage, setSkuCurrentPage] = useState(0);
  const [skuTotalPages, setSkuTotalPages] = useState(0);
  const [isSkuLoading, setIsSkuLoading] = useState(true);
  const [isFamilyLoading, setIsFamilyLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [skuSortConfig, setSkuSortConfig] = useState({
    key: null,
    direction: null,
  });
  const [forecastSortConfig, setForecastSortConfig] = useState({
    key: null,
    direction: null,
  });
  const [skuSuggestions, setSkuSuggestions] = useState([]);
  const [activeSuggestionRowId, setActiveSuggestionRowId] = useState(null);
  const [isSearchingSku, setIsSearchingSku] = useState(false);
  const searchDebounceRef = useRef(null);

  const [isCompareActive, setIsCompareActive] = useState(false);

  const [familyViewMode, setFamilyViewMode] = useState("constrained");
  const [isFamilyCompareActive, setIsFamilyCompareActive] = useState(false);
  const [skuViewMode, setSkuViewMode] = useState("constrained");
  const [isSkuCompareActive, setIsSkuCompareActive] = useState(false);
  const [unconstrainedFamilyData, setUnconstrainedFamilyData] = useState(null);
  const [unconstrainedSkuData, setUnconstrainedSkuData] = useState(null);

  // Ordine colonne Family
  const [familyColumnOrder, setFamilyColumnOrder] = useStickyState(
    optionalFamilyColumns,
    "globalView_familyColumnOrder",
  );
  // Ordine colonne SKU
  const [skuColumnOrder, setSkuColumnOrder] = useStickyState(
    optionalSkuColumns,
    "globalView_skuColumnOrder_v5",
  );

  // Visibilità Forecast (calcoliamo il default se non c'è in storage)
  const [columnVisibility, setColumnVisibility] = useStickyState(
    (() => {
      const initialState = forecastColumns.reduce(
        (acc, col) => ({ ...acc, [col]: true }),
        {},
      );
      initialState["ACT QTY"] = false;
      return initialState;
    })(),
    "globalView_forecastVisibility_v2",
  );

  const [winrateColumnVisibility, setWinrateColumnVisibility] = useStickyState(
    winrateColumns.reduce((acc, col) => ({ ...acc, [col]: true }), {}),
    "globalView_winrateVisibility_v3",
  );

  // Visibilità SKU
  const [skuColumnVisibility, setSkuColumnVisibility] = useStickyState(
    optionalSkuColumns.reduce((acc, col) => ({ ...acc, [col]: true }), {}),
    "globalView_skuVisibility_v5",
  );

  // Visibilità Family
  const [familyColumnVisibility, setFamilyColumnVisibility] = useStickyState(
    optionalFamilyColumns.reduce((acc, col) => ({ ...acc, [col]: true }), {}),
    "globalView_familyVisibility",
  );

  const [isFamilyColumnDropdownOpen, setIsFamilyColumnDropdownOpen] =
    useState(false);

  const [isColumnDropdownOpen, setIsColumnDropdownOpen] = useState(false);
  const [isSkuColumnDropdownOpen, setIsSkuColumnDropdownOpen] = useState(false);

  const forecastSectionRef = useRef(null);

  const filteredForecastData = useMemo(
    () => aggregateByWeek(forecastData, customerType),
    [forecastData, customerType]
  );
  const filteredWinrateData = useMemo(
    () => aggregateWinrateByWeek(winrateData, forecastData, customerType),
    [winrateData, forecastData, customerType]
  );

  const [isWinrateCompareActive, setIsWinrateCompareActive] = useState(false);
  const [oppositeWinrateData, setOppositeWinrateData] = useState([]);

  useEffect(() => {
    setOppositeWinrateData([]);
    if (!isWinrateCompareActive) return undefined;
    let cancelled = false;
    fetchGlobalWinrate({
      page_size: 3000,
      customer_type: customerType,
      availability: availabilityType === "available" ? "unavailable" : "available",
    })
      .then((res) => {
        if (!cancelled) setOppositeWinrateData(res?.forecast_result || []);
      })
      .catch((err) => console.error("Failed to fetch comparison win rate:", err));
    return () => {
      cancelled = true;
    };
  }, [isWinrateCompareActive, customerType, availabilityType]);

  const winrateCompareMap = useMemo(() => {
    if (!isWinrateCompareActive) return null;
    const runId = winrateRuns[selectedWinrateGenIndex]?.run_id;
    const rows = oppositeWinrateData.filter((r) => r.run_id === runId);
    return new Map(
      aggregateWinrateByWeek(rows, forecastData, customerType).map((r) => [r.year_week, r]),
    );
  }, [isWinrateCompareActive, oppositeWinrateData, winrateRuns, selectedWinrateGenIndex, forecastData, customerType]);

  useEffect(() => {
    const reconcile = (setter, columns) =>
      setter((prev) => {
        const next = {};
        let changed = Object.keys(prev).some((key) => !columns.includes(key));
        columns.forEach((col) => {
          if (prev[col] === undefined) changed = true;
          next[col] = prev[col] === undefined ? true : prev[col];
        });
        return changed ? next : prev;
      });
    reconcile(setColumnVisibility, forecastColumns);
    reconcile(setWinrateColumnVisibility, winrateColumns);
  }, [setColumnVisibility, setWinrateColumnVisibility]);

  const showWrCol = (col) => winrateColumnVisibility[col];

  const comparisonData = useMemo(() => {
    if (!isCompareActive) return null;
    if (forecastMode === "demand") {
      if (availableRuns.length === 0) return null;
      const currentRunId = availableRuns[0].run_id;
      return aggregateByWeek(
        allForecastData.filter((row) => row.run_id === currentRunId),
        customerType
      );
    } else {
      if (winrateRuns.length === 0) return null;
      const currentRunId = winrateRuns[0].run_id;
      const compDemand = availableRuns.length > 0
        ? allForecastData.filter((row) => row.run_id === availableRuns[0].run_id)
        : forecastData;
      return aggregateWinrateByWeek(
        allWinrateData.filter((row) => row.run_id === currentRunId),
        compDemand,
        customerType
      );
    }
  }, [isCompareActive, forecastMode, availableRuns, allForecastData, winrateRuns, allWinrateData, customerType, forecastData]);

  const processForecastData = (rawData) => {
    if (!rawData || rawData.length === 0) return { runs: [], sortedData: [] };

    // Identifica le run univoche
    const runMap = new Map();

    rawData.forEach((row) => {
      if (!runMap.has(row.run_id)) {
        runMap.set(row.run_id, {
          run_id: row.run_id,
          run_date: row.run_date,
          parsedDate: new Date(row.run_date),
        });
      }
    });

    const sortedRuns = Array.from(runMap.values()).sort((a, b) => {
      return b.parsedDate - a.parsedDate;
    });

    return { runs: sortedRuns, sortedData: rawData };
  };

  const processFamilyResponse = (data) => {
    let list = [];
    if (data?.family_list) list = data.family_list;
    else if (data?.family_details) list = data.family_details;
    else if (Array.isArray(data)) list = data;

    return list
      .filter(Boolean)
      .map((d) => ({
        family: d.family,
        family_description: d.family_description,
        roi: d.roi?.current,
        min_roi: d.roi?.min_roi,
        service_level: d.service_level?.current,
        gross_margin: d.gross_margin?.current,
        net_margin: d.net_margin?.current,
        budget: d.budget,
        revenue: d.revenue,
        cogs: d.cogs,
        max_budget: d.max_budget,
        run_id: d.run_id,
        cost: d.cost,
        shelf_stockout_rate: d.shelf_stockout_rate,
        pna_availability: d.pna_availability,
      }));
  };

  const loadFamilyData = useCallback(async () => {
    setIsFamilyLoading(true);
    setCurrentFamilyRunId(null);

    try {
      const [constrainedRes, unconstrainedRes] = await Promise.allSettled([
        fetchFamilyDetailsByMode("constrained"),
        fetchFamilyDetailsByMode("unconstrained"),
      ]);

      let constrainedRunId = null;

      if (constrainedRes.status === "fulfilled") {
        const processed = processFamilyResponse(constrainedRes.value);
        setFamilyTableData(processed);
        setPieChartData(processed);
        if (processed.length > 0 && processed[0].run_id) {
          constrainedRunId = processed[0].run_id;
          setCurrentFamilyRunId(constrainedRunId);
        }
      } else {
        console.error("Failed to fetch constrained family data:", constrainedRes.reason);
        setFamilyTableData([]);
      }

      if (unconstrainedRes.status === "fulfilled") {
        let uncProcessed = processFamilyResponse(unconstrainedRes.value);
        if (constrainedRunId) {
          uncProcessed = uncProcessed.filter((r) => r.run_id === constrainedRunId);
        }
        setUnconstrainedFamilyData(uncProcessed);
      } else {
        console.error("Failed to fetch unconstrained family data:", unconstrainedRes.reason);
        setUnconstrainedFamilyData(null);
      }
    } catch (error) {
      console.error("Failed to fetch Family data:", error);
      setFamilyTableData([]);
    } finally {
      setIsFamilyLoading(false);
    }
  }, []);

  const handleSelectSkuSuggestion = (rowId, selectedSku) => {
    setSkuFilterInputs((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, value: selectedSku } : row,
      ),
    );

    // Chiudi i suggerimenti
    setSkuSuggestions([]);
    setActiveSuggestionRowId(null);

    const updatedInputs = skuFilterInputs.map((row) =>
      row.id === rowId ? { ...row, value: selectedSku } : row,
    );

    const filters = {};
    updatedInputs.forEach(({ column, operator, value }) => {
      if (column === "sku_id") {
        filters["sku_id"] = value;
      } else {
        const cleanedValue = String(value)
          .replace(/[%$]/g, "")
          .replace(/,/g, "");
        if (cleanedValue.trim() !== "" && !isNaN(parseFloat(cleanedValue))) {
          const numericValue = parseFloat(cleanedValue);
          if (operator === ">") filters[`${column}_min`] = numericValue;
          else if (operator === "<") filters[`${column}_max`] = numericValue;
        }
      }
    });

    setSkuCurrentPage(0);
    setAppliedSkuFilters(filters);
  };

  const loadSkuData = useCallback(
    async (page = 0, filters = {}, sort = { key: null, direction: null }) => {
      setIsSkuLoading(true);

      try {
        const params = {
          ...filters,
          page_n: page,
          last_run_only: true,
        };

        if (sort.key && sort.direction) {
          params.order_by = sort.key;
          params.order = sort.direction;
        }

        const skuData = await fetchSkuList(params);

        if (skuData?.sku_list && skuData?.pagination) {
          const constrained = skuData.sku_list.filter(
            (r) => r.optimization_mode === "constrained",
          );
          const unconstrained = skuData.sku_list.filter(
            (r) => r.optimization_mode === "unconstrained",
          );
          setSkuTableData(constrained);
          setUnconstrainedSkuData(unconstrained);
          setSkuCurrentPage(skuData.pagination.current_page);
          setSkuTotalPages(skuData.pagination.total_pages);
        } else {
          setSkuTableData([]);
          setUnconstrainedSkuData(null);
          setSkuCurrentPage(0);
          setSkuTotalPages(0);
        }
      } catch (error) {
        console.error("Failed to fetch SKU list:", error);
        setSkuTableData([]);
        setUnconstrainedSkuData(null);
      } finally {
        setIsSkuLoading(false);
      }
    },
    [],
  );

  const unconstrainedFamilyMap = useMemo(() => {
    if (!unconstrainedFamilyData) return new Map();
    const map = new Map();
    unconstrainedFamilyData.forEach((r) => map.set(r.family, r));
    return map;
  }, [unconstrainedFamilyData]);

  const unconstrainedSkuMap = useMemo(() => {
    if (!unconstrainedSkuData) return new Map();
    const map = new Map();
    unconstrainedSkuData.forEach((r) => map.set(r.sku_id, r));
    return map;
  }, [unconstrainedSkuData]);

  const hasFamilyUnconstrained = unconstrainedFamilyData !== null && unconstrainedFamilyData.length > 0;
  const hasSkuUnconstrained = unconstrainedSkuData !== null && unconstrainedSkuData.length > 0;

  const sortedFamilyData = useMemo(() => {
    const source =
      familyViewMode === "unconstrained" && unconstrainedFamilyData
        ? unconstrainedFamilyData
        : familyTableData;
    let sortableItems = [...source];

    Object.entries(appliedFamilyFilters).forEach(([filterKey, filterVal]) => {
      const match = filterKey.match(/^(.+)_(min|max)$/);
      if (match) {
        const [, col, bound] = match;
        sortableItems = sortableItems.filter((row) => {
          const v = parseFloat(row[col]);
          if (isNaN(v)) return false;
          return bound === "min" ? v > filterVal : v < filterVal;
        });
      }
    });

    if (sortConfig.key !== null && sortConfig.direction !== null) {
      sortableItems.sort((a, b) => {
        const key = sortConfig.key;
        let aValue = a[key];
        let bValue = b[key];

        if (aValue == null) aValue = "";
        if (bValue == null) bValue = "";

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [familyTableData, unconstrainedFamilyData, familyViewMode, sortConfig, appliedFamilyFilters]);

  const effectiveSkuData = useMemo(() => {
    if (skuViewMode === "unconstrained" && unconstrainedSkuData) {
      return unconstrainedSkuData;
    }
    return skuTableData;
  }, [skuTableData, unconstrainedSkuData, skuViewMode]);

  const requestSort = (key) => {
    let direction = "asc";
    // Logica ciclica: asc -> desc -> null (reset)
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = null; // Reset
    }
    setSortConfig({ key: direction ? key : null, direction });
  };

  const requestSkuSort = (key) => {
    let direction = "asc";

    // Logica ciclica: asc -> desc -> null (reset/default)
    if (skuSortConfig.key === key && skuSortConfig.direction === "asc") {
      direction = "desc";
    } else if (
      skuSortConfig.key === key &&
      skuSortConfig.direction === "desc"
    ) {
      direction = null;
    }

    setSkuSortConfig({ key: direction ? key : null, direction });
    setSkuCurrentPage(0); // Resetta sempre alla prima pagina quando cambi ordinamento
  };

  // Indicatore freccia (▲ / ▼)
  const getSkuSortIndicator = (key) => {
    if (skuSortConfig.key !== key) return null;
    return skuSortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  // Helper per mostrare la freccia nell'header
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    if (sortConfig.direction === "asc") return " ▲";
    if (sortConfig.direction === "desc") return " ▼";
    return null;
  };

  // Dati filtrati e ordinati
  const sortedForecastData = useMemo(() => {
    let processedData = [...filteredForecastData].filter((row) => {
      return (
        (row.fcst_qty_avg != null && row.fcst_qty_avg !== 0) ||
        (row.fcst_qty_lowerbound != null && row.fcst_qty_lowerbound !== 0) ||
        (row.fcst_qty_upperbound != null && row.fcst_qty_upperbound !== 0) ||
        (row.fcst_val_avg != null && row.fcst_val_avg !== 0) ||
        (row.fcst_val_lowerbound != null && row.fcst_val_lowerbound !== 0) ||
        (row.fcst_val_upperbound != null && row.fcst_val_upperbound !== 0) ||
        (row.actual_qty_last_year != null && row.actual_qty_last_year !== 0) ||
        (row.actual_val_last_year != null && row.actual_val_last_year !== 0)
      );
    });

    if (forecastSortConfig.key && forecastSortConfig.direction) {
      const dataKey = forecastMap[forecastSortConfig.key];

      processedData.sort((a, b) => {
        let aValue = a[dataKey];
        let bValue = b[dataKey];

        if (aValue == null)
          aValue = typeof bValue === "string" ? "" : -Infinity;
        if (bValue == null)
          bValue = typeof aValue === "string" ? "" : -Infinity;

        if (aValue < bValue)
          return forecastSortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue)
          return forecastSortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    } else {
      processedData.sort((a, b) => a.year_week.localeCompare(b.year_week));
    }

    return processedData;
  }, [filteredForecastData, forecastSortConfig]);

  const sortedWinrateData = useMemo(() => {
    let processedData = [...filteredWinrateData].filter(
      (row) => (row.fcst_qty_avg != null && row.fcst_qty_avg !== 0) || (row.actual_wr_last_year != null && row.actual_wr_last_year !== 0)
    );

    if (forecastSortConfig.key && forecastSortConfig.direction) {
      const dataKey = winrateMap[forecastSortConfig.key];
      if (dataKey) {
        processedData.sort((a, b) => {
          let aValue = a[dataKey];
          let bValue = b[dataKey];
          if (aValue == null) aValue = -Infinity;
          if (bValue == null) bValue = -Infinity;
          if (aValue < bValue) return forecastSortConfig.direction === "asc" ? -1 : 1;
          if (aValue > bValue) return forecastSortConfig.direction === "asc" ? 1 : -1;
          return 0;
        });
      }
    } else {
      processedData.sort((a, b) => a.year_week.localeCompare(b.year_week));
    }

    return processedData;
  }, [filteredWinrateData, forecastSortConfig]);

  // Funzione Trigger Click
  const requestForecastSort = (colName) => {
    let direction = "asc";
    if (
      forecastSortConfig.key === colName &&
      forecastSortConfig.direction === "asc"
    ) {
      direction = "desc";
    } else if (
      forecastSortConfig.key === colName &&
      forecastSortConfig.direction === "desc"
    ) {
      direction = null; // Reset
    }
    setForecastSortConfig({ key: direction ? colName : null, direction });
  };

  // Indicatore Freccia
  const getForecastSortIndicator = (colName) => {
    if (forecastSortConfig.key !== colName) return null;
    return forecastSortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  // Close user menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setIsUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleLogout() {
    logout();
    navigate("/login");
  }

  useEffect(() => {
    const loadPieData = async () => {
      try {
        const familyData = await fetchFamilyDetailsByMode("constrained");
        const processed = processFamilyResponse(familyData);
        setPieChartData(processed);
      } catch (error) {
        console.error("Error loading pie chart data:", error);
      }
    };

    loadPieData();
  }, []);

  // Effetto 1: Carica i dati globali una sola volta al montaggio del componente
  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      try {
        // Scarichiamo la configurazione e la lista delle run DISPONIBILI
        const [configResult, runListResult] = await Promise.allSettled([
          fetchConfig(),
          fetchRunList("optimization_global"),
        ]);

        const configResponse = configResult.status === "fulfilled" ? configResult.value : null;
        const runListResponse = runListResult.status === "fulfilled" ? runListResult.value : null;

        if (configResponse) {
          setConfig(configResponse);
        }

        // Elaboriamo e ordiniamo le run per data (dal più vecchio al più recente per i grafici)
        let sortedRuns = [];
        if (runListResponse?.run_list) {
          sortedRuns = runListResponse.run_list
            .map((run) => ({
              ...run,
              parsedDate: new Date(run.date_start), // Usiamo date_start per l'ordinamento
            }))
            .sort((a, b) => a.parsedDate - b.parsedDate); // Ordinamento Crescente (Vecchio -> Nuovo)
        }

        if (sortedRuns.length > 5) {
          sortedRuns = sortedRuns.slice(-5);
        }

        // Se non ci sono run, gestiamo l'errore o stato vuoto
        if (sortedRuns.length === 0) {
          console.warn("Nessuna run globale trovata.");
          setGlobalView(null);
          setKpiHistory(null);
          setLoading(false);
          return;
        }

        // Scarichiamo i dettagli PER OGNI RUN trovata (per costruire lo storico)
        // più i constraints e il forecast (parallelamente)
        const constrainedPromises = sortedRuns.map((run) =>
          fetchGlobalView(run.run_id, "constrained"),
        );
        const unconstrainedPromises = sortedRuns.map((run) =>
          fetchGlobalView(run.run_id, "unconstrained"),
        );

        const results = await Promise.allSettled([
          ...constrainedPromises,
          ...unconstrainedPromises,
          fetchGlobalConstraints(),
        ]);

        // --- GESTIONE DEI RISULTATI ---

        const constrainedResults = results.slice(0, sortedRuns.length);
        const unconstrainedResults = results.slice(sortedRuns.length, sortedRuns.length * 2);

        // Indice per Constraints
        const constraintsIndex = sortedRuns.length * 2;

        const buildHistory = (viewResults) => {
          const h = {
            roi: [], netMargin: [], grossMargin: [], cost: [],
            sl: [], pna: [], shelfInventory: [],
            runLabels: [], rawData: [],
          };
          viewResults.forEach((result, index) => {
            const runInfo = sortedRuns[index];
            const dateLabel = runInfo.date_start
              ? runInfo.date_start.split(" ")[0]
              : runInfo.run_id;
            if (result.status === "fulfilled" && result.value?.global_view) {
              const data = result.value.global_view;
              h.roi.push(data.roi.current);
              h.netMargin.push(data.net_margin.current);
              h.grossMargin.push(data.gross_margin.current);
              h.cost.push(data.cost.current);
              h.sl.push(data.service_level.current);
              h.runLabels.push(dateLabel);
              h.rawData.push(data);
              h.pna.push(data.pna_availability);
              h.shelfInventory.push(data.shelf_inventory_value_growth);
            } else {
              h.roi.push(null);
              h.netMargin.push(null);
              h.grossMargin.push(null);
              h.cost.push(null);
              h.sl.push(null);
              h.runLabels.push(dateLabel);
              h.pna.push(null);
              h.shelfInventory.push(null);
            }
          });
          return h;
        };

        const history = buildHistory(constrainedResults);
        setKpiHistory(history);
        setUnconstrainedKpiHistory(buildHistory(unconstrainedResults));

        // Impostiamo la GlobalView "Corrente" (L'ultima della lista ordinata)
        // Cerchiamo l'ultimo risultato valido partendo dalla fine
        let currentView = null;
        for (let i = constrainedResults.length - 1; i >= 0; i--) {
          if (
            constrainedResults[i].status === "fulfilled" &&
            constrainedResults[i].value?.global_view
          ) {
            currentView = constrainedResults[i].value.global_view;
            break;
          }
        }
        setGlobalView(currentView);

        // Gestione Constraints
        if (
          results[constraintsIndex].status === "fulfilled" &&
          results[constraintsIndex].value?.global_constraints
        ) {
          const gc = { ...results[constraintsIndex].value.global_constraints };
          setGlobalConstraints(gc);
        } else {
          setGlobalConstraints({});
        }

      } catch (error) {
        console.error(
          "Errore durante il caricamento della Global View:",
          error,
        );
        // Don't block the page — let sections that loaded show their data
        console.warn("Some data failed to load — page will render with available data");
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, []);

  const [isForecastLoading, setIsForecastLoading] = useState(false);
  useEffect(() => {
    const loadForecastAndWinrate = async () => {
      setIsForecastLoading(true);
      const [fResult, wResult] = await Promise.allSettled([
        fetchGlobalForecast({ page_size: 3000, customer_type: customerType }),
        fetchGlobalWinrate({
          page_size: 3000,
          customer_type: customerType,
          availability: availabilityType,
        }),
      ]);

      if (fResult.status === "fulfilled") {
        const rawRows = fResult.value?.forecast_result || [];
        const { runs } = processForecastData(rawRows);
        setAllForecastData(rawRows);
        setAvailableRuns(runs);
        if (runs.length > 0) {
          const currentRunId = runs[0].run_id;
          setForecastData(rawRows.filter((r) => r.run_id === currentRunId));
          setSelectedGenerationIndex(0);
        } else {
          setForecastData([]);
        }
      }

      if (wResult.status === "fulfilled") {
        const rawRows = wResult.value?.forecast_result || [];
        const { runs } = processForecastData(rawRows);
        setAllWinrateData(rawRows);
        setWinrateRuns(runs);
        if (runs.length > 0) {
          const currentRunId = runs[0].run_id;
          setWinrateData(rawRows.filter((r) => r.run_id === currentRunId));
          setSelectedWinrateGenIndex(0);
        } else {
          setWinrateData([]);
        }
      }
      setIsForecastLoading(false);
    };
    loadForecastAndWinrate();
  }, [customerType, availabilityType]);

  useEffect(() => {
    loadFamilyData();
  }, [loadFamilyData]);

  // Effetto 3: Carica i dati degli SKU quando i filtri o la pagina cambiano
  useEffect(() => {
    loadSkuData(skuCurrentPage, appliedSkuFilters, skuSortConfig);
  }, [skuCurrentPage, appliedSkuFilters, skuSortConfig, loadSkuData]);

  useEffect(() => {
    if (!config) return;

    // 1. Applica visibilità Family Table (SOLO se non c'è già una preferenza salvata)
    const hasSavedFamilyConfig = window.localStorage.getItem(
      "globalView_familyVisibility",
    );
    const familyColsConfig = config.pageLayouts?.global?.familyTableColumns;

    if (familyColsConfig && !hasSavedFamilyConfig) {
      setFamilyColumnVisibility((prev) => {
        const newVisibility = { ...prev };
        familyColsConfig.forEach((col) => {
          const mappedName = familyConfigMap[col.id];
          if (mappedName) {
            newVisibility[mappedName] = col.visible;
          }
        });
        return newVisibility;
      });
    }

    // --- AUTO-AGGIORNAMENTO COLONNE FAMILY ---
    // Inietta automaticamente nuove colonne negli utenti con localStorage vecchio

    // 1. Aggiorna l'ordine
    setFamilyColumnOrder((prevOrder) => {
      const missingColumns = optionalFamilyColumns.filter(
        (col) => !prevOrder.includes(col),
      );
      if (missingColumns.length > 0) {
        return [...prevOrder, ...missingColumns];
      }
      return prevOrder;
    });

    // 2. Aggiorna la visibilità
    setFamilyColumnVisibility((prevVis) => {
      let hasChanges = false;
      const newVisibility = { ...prevVis };

      optionalFamilyColumns.forEach((col) => {
        if (newVisibility[col] === undefined) {
          newVisibility[col] = true;
          hasChanges = true;
        }
      });

      return hasChanges ? newVisibility : prevVis;
    });

    // 2. Applica visibilità SKU Table (SOLO se non c'è già una preferenza salvata)
    const hasSavedSkuConfig = window.localStorage.getItem(
      "globalView_skuVisibility",
    );
    const skuColsConfig = config.pageLayouts?.global?.skuTableColumns;

    if (skuColsConfig && !hasSavedSkuConfig) {
      setSkuColumnVisibility((prev) => {
        const newVisibility = { ...prev };
        skuColsConfig.forEach((col) => {
          const mappedName = skuConfigMap[col.id];
          if (mappedName) {
            newVisibility[mappedName] = col.visible;
          }
        });
        return newVisibility;
      });
    }
    // --- AUTO-AGGIORNAMENTO COLONNE SKU ---
    // Se abbiamo aggiunto nuove colonne nel codice, gli utenti con il localStorage vecchio non le vedrebbero.
    // Questo script le cerca e le inietta automaticamente mantenendo le preferenze esistenti.

    // 1. Aggiorna l'ordine
    setSkuColumnOrder((prevOrder) => {
      const cleanOrder = prevOrder.filter((col) =>
        optionalSkuColumns.includes(col),
      );
      const missingColumns = optionalSkuColumns.filter(
        (col) => !cleanOrder.includes(col),
      );

      if (cleanOrder.length !== prevOrder.length || missingColumns.length > 0) {
        return [...cleanOrder, ...missingColumns];
      }
      return prevOrder;
    });

    // 2. Aggiorna la visibilità
    setSkuColumnVisibility((prevVis) => {
      let hasChanges = false;
      const newVisibility = {};

      // Mantiene solo le chiavi valide
      Object.keys(prevVis).forEach((key) => {
        if (optionalSkuColumns.includes(key)) {
          newVisibility[key] = prevVis[key];
        } else {
          hasChanges = true;
        }
      });

      optionalSkuColumns.forEach((col) => {
        if (newVisibility[col] === undefined) {
          newVisibility[col] = true;
          hasChanges = true;
        }
      });

      return hasChanges ? newVisibility : prevVis;
    });
  }, [
    config,
    setFamilyColumnVisibility,
    setSkuColumnVisibility,
    setFamilyColumnOrder,
  ]);

  // *** CHIUDE IL DROPDOWN SE SI CLICCA FUORI ***
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        (isDownloadDropdownOpen ||
          isWinrateDropdownOpen ||
          isFamilyDownloadDropdownOpen ||
          isSkuDownloadDropdownOpen) &&
        !event.target.closest(".split-btn-container")
      ) {
        setIsDownloadDropdownOpen(false);
        setIsWinrateDropdownOpen(false);
        setIsFamilyDownloadDropdownOpen(false);
        setIsSkuDownloadDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    isDownloadDropdownOpen,
    isWinrateDropdownOpen,
    isFamilyDownloadDropdownOpen,
    isSkuDownloadDropdownOpen,
  ]);

  // Chiude i dropdown se si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Family Table Dropdown
      if (
        familyColsRef.current &&
        !familyColsRef.current.contains(event.target)
      ) {
        setIsFamilyColumnDropdownOpen(false);
      }
      // SKU Table Dropdown
      if (skuColsRef.current && !skuColsRef.current.contains(event.target)) {
        setIsSkuColumnDropdownOpen(false);
      }
      // Forecast Table Dropdown
      if (
        forecastColsRef.current &&
        !forecastColsRef.current.contains(event.target)
      ) {
        setIsColumnDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // FUNZIONE: Gestisce lo spostamento tramite Drag & Drop (Family)
  const handleFamilyOnDrop = (draggedIndex, targetIndex) => {
    if (draggedIndex === targetIndex) return;

    const newOrder = [...familyColumnOrder];
    const draggedItem = newOrder[draggedIndex];

    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);

    setFamilyColumnOrder(newOrder);
  };

  // FUNZIONE: Gestisce lo spostamento tramite Drag & Drop (SKU)
  const handleSkuOnDrop = (draggedIndex, targetIndex) => {
    if (draggedIndex === targetIndex) return;

    const newOrder = [...skuColumnOrder];
    const draggedItem = newOrder[draggedIndex];

    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);

    setSkuColumnOrder(newOrder);
  };

  const handleDownloadFamily = async () => {
    setIsDownloadingFamily(true);
    try {
      const downloadParams = {
        ...appliedFamilyFilters,
        run_id: currentFamilyRunId,
        order_by: sortConfig.key || "family",
        order: sortConfig.direction || "asc",
        last_run_only: true,
        optimization_mode: familyViewMode,
      };

      await downloadFamilyList(downloadParams, `Overview_By_Family_${familyViewMode}.xlsx`);
    } catch (error) {
      console.error(error);
      alert("Error downloading file.");
    } finally {
      setIsDownloadingFamily(false);
    }
  };

  const handleDownloadFamilyUnfiltered = async () => {
    setIsFamilyDownloadDropdownOpen(false);
    setIsDownloadingFamily(true);
    try {
      const downloadParams = {
        run_id: currentFamilyRunId,
        order_by: sortConfig.key || "family",
        order: sortConfig.direction || "asc",
        last_run_only: true,
        optimization_mode: familyViewMode,
      };

      await downloadFamilyList(
        downloadParams,
        `Overview_By_Family_${familyViewMode}_unfiltered.xlsx`,
      );
    } catch (error) {
      console.error(error);
      alert("Error downloading file.");
    } finally {
      setIsDownloadingFamily(false);
    }
  };

  const handleDownloadSku = async () => {
    setIsDownloadingSku(true);
    try {
      // Uniamo i filtri applicati con l'ordinamento corrente
      const downloadParams = {
        ...appliedSkuFilters,
        order_by: skuSortConfig.key || "sku_id",
        order: skuSortConfig.direction || "asc",
        last_run_only: true,
        optimization_mode: skuViewMode,
      };

      await downloadSkuList(downloadParams, `Overview_By_SKU_${skuViewMode}.xlsx`);
    } catch (error) {
      console.error(error);
      alert("Error downloading SKU file.");
    } finally {
      setIsDownloadingSku(false);
    }
  };

  const handleDownloadSkuUnfiltered = async () => {
    setIsSkuDownloadDropdownOpen(false);
    setIsDownloadingSku(true);
    try {
      const downloadParams = {
        order_by: skuSortConfig.key || "sku_id",
        order: skuSortConfig.direction || "asc",
        last_run_only: true,
        optimization_mode: skuViewMode,
      };

      await downloadSkuList(
        downloadParams,
        `Overview_By_SKU_${skuViewMode}_unfiltered.xlsx`,
      );
    } catch (error) {
      console.error(error);
      alert("Error downloading SKU file.");
    } finally {
      setIsDownloadingSku(false);
    }
  };

  const handleDownloadForecast = async () => {
    if (!availableRuns[selectedGenerationIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRun = availableRuns[selectedGenerationIndex];
    const currentRunId = currentRun.run_id;

    const filename = `global_forecast_${currentRunId}_${customerType}.xlsx`;

    setIsDownloadingForecast(true);
    try {
      await downloadGlobalForecast({ run_id: currentRunId, customer_type: customerType }, filename);
    } catch (error) {
      console.error(error);
      alert("Error downloading forecast file.");
    } finally {
      setIsDownloadingForecast(false);
    }
  };

  const handleDownloadForecastUnfiltered = async () => {
    setIsDownloadDropdownOpen(false);
    if (!availableRuns[selectedGenerationIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRunId = availableRuns[selectedGenerationIndex].run_id;
    const filename = `global_forecast_${currentRunId}_unfiltered.xlsx`;

    setIsDownloadingForecast(true);
    try {
      await downloadGlobalForecast({ run_id: currentRunId }, filename);
    } catch (error) {
      console.error(error);
      alert("Error downloading forecast file.");
    } finally {
      setIsDownloadingForecast(false);
    }
  };

  // *** HANDLE DOWNLOAD ALL SKUS ***
  const handleDownloadAllSkus = async () => {
    if (!availableRuns[selectedGenerationIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRun = availableRuns[selectedGenerationIndex];
    const currentRunId = currentRun.run_id;

    setIsDownloadingAllSkus(true);

    try {
      const filename = `all_skus_forecast_${currentRunId}_unfiltered.xlsx`;
      await downloadAllSkusForecast({ run_id: currentRunId }, filename);
    } catch (error) {
      console.error(error);
      alert("Error downloading all SKUs file.");
    } finally {
      setIsDownloadingAllSkus(false);
    }
  };

  const handleDownloadWinrate = async () => {
    if (!winrateRuns[selectedWinrateGenIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRun = winrateRuns[selectedWinrateGenIndex];
    const currentRunId = currentRun.run_id;

    const filename = `global_winrate_${currentRunId}_${customerType}_${availabilityType}.xlsx`;

    setIsDownloadingWinrate(true);
    try {
      await downloadGlobalWinrate({ run_id: currentRunId, customer_type: customerType, availability: availabilityType }, filename);
    } catch (error) {
      console.error(error);
      alert("Error downloading win rate file.");
    } finally {
      setIsDownloadingWinrate(false);
    }
  };

  const handleDownloadWinrateUnfiltered = async () => {
    setIsWinrateDropdownOpen(false);
    if (!winrateRuns[selectedWinrateGenIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRunId = winrateRuns[selectedWinrateGenIndex].run_id;
    const filename = `global_winrate_${currentRunId}_unfiltered.xlsx`;

    setIsDownloadingWinrate(true);
    try {
      await downloadGlobalWinrate({ run_id: currentRunId, availability: "all" }, filename);
    } catch (error) {
      console.error(error);
      alert("Error downloading win rate file.");
    } finally {
      setIsDownloadingWinrate(false);
    }
  };

  const handleDownloadAllSkusWinrate = async () => {
    if (!winrateRuns[selectedWinrateGenIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRun = winrateRuns[selectedWinrateGenIndex];
    const currentRunId = currentRun.run_id;

    setIsDownloadingAllSkusWinrate(true);
    try {
      const filename = `all_skus_winrate_${currentRunId}_unfiltered.xlsx`;
      await downloadAllSkusWinrate({ run_id: currentRunId, availability: "all" }, filename);
    } catch (error) {
      console.error(error);
      alert("Error downloading all SKUs win rate file.");
    } finally {
      setIsDownloadingAllSkusWinrate(false);
    }
  };

  const formatCurrency = (num) => {
    // Se il valore è null o undefined, mostra N/E
    if (num === null || num === undefined) return "N/A";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(num);
  };
  const formatUnitCurrency = (num) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num || 0);

  const formatNumber = (num) => {
    // Se il valore è null o undefined, mostra N/E
    if (num === null || num === undefined) return "N/A";

    return Math.round(Number(num)).toLocaleString("en-US");
  };

  const formatPercentage = (num) => {
    // Se il valore è null, undefined o non è un numero, mostra N/E
    if (num === null || num === undefined || typeof num !== "number") {
      return "N/A";
    }
    return `${num.toFixed(2)}%`;
  };

  const COST_FIELDS = new Set([
    "budget",
    "cost",
    "cogs",
    "shelf_stockout_rate",
    "inbound_costs",
    "outbound_costs",
    "carry_costs",
    "unit_cost",
    "margin_adjusters",
    "safety_stock",
  ]);

  const renderCompareCell = (def, baseRow, targetRow, ...fmts) => {
    if (!targetRow || targetRow === baseRow) return def.render(baseRow, ...fmts);

    const rawBase = baseRow[def.sortKey];
    const rawTarget = targetRow[def.sortKey];

    if (rawBase == null || rawTarget == null || typeof rawBase !== "number") {
      return def.render(baseRow, ...fmts);
    }

    const baseVal = def.render(baseRow, ...fmts);
    const targetVal = def.render(targetRow, ...fmts);
    const delta = rawTarget - rawBase;
    const isImprovement = COST_FIELDS.has(def.sortKey)
      ? delta < 0
      : delta > 0;
    const deltaClass =
      delta === 0
        ? "compare-neutral"
        : isImprovement
          ? "compare-improvement"
          : "compare-degradation";
    const sign = delta > 0 ? "+" : "";
    const deltaLabel =
      delta === 0
        ? "0%"
        : rawBase !== 0
          ? `${sign}${((delta / Math.abs(rawBase)) * 100).toFixed(1)}%`
          : "—";

    const tooltip = `${baseVal} → ${targetVal} (${deltaLabel})`;

    return (
      <span className="compare-cell-content" title={tooltip}>
        <span className="compare-target">{targetVal}</span>
        <span className={`compare-delta ${deltaClass}`}>{deltaLabel}</span>
      </span>
    );
  };

  const renderWinrateCell = (row, field) => {
    const format = (v) => (v != null ? (v * 100).toFixed(1) + "%" : "—");
    const base = row[field];
    const target = isWinrateCompareActive ? winrateCompareMap?.get(row.year_week)?.[field] : null;
    if (base == null || target == null) return format(base);
    const delta = target - base;
    const deltaClass =
      delta === 0 ? "compare-neutral" : delta > 0 ? "compare-improvement" : "compare-degradation";
    const deltaLabel =
      delta === 0
        ? "0%"
        : base !== 0
          ? `${delta > 0 ? "+" : ""}${((delta / Math.abs(base)) * 100).toFixed(1)}%`
          : "—";
    return (
      <span
        className="compare-cell-content"
        title={
          availabilityType === "available"
            ? `Available ${format(base)} → Unavailable ${format(target)} (${deltaLabel})`
            : `Unavailable ${format(base)} → Available ${format(target)} (${deltaLabel})`
        }
      >
        <span className="compare-target">{format(base)}</span>
        <span className={`compare-delta ${deltaClass}`}>{deltaLabel}</span>
      </span>
    );
  };

  const getKpiColorClass = (kpiId) => {
    if (!globalView || !globalConstraints) return "";
    const view = globalView;
    const constraints = globalConstraints;
    switch (kpiId) {
      case "roi":
        if (view.roi.min_roi === null || view.roi.min_roi === undefined)
          return "";
        return view.roi.current >= view.roi.min_roi
          ? "positive-value"
          : "negative-value";
      case "budget":
        if (
          view.max_budget.current === null ||
          view.max_budget.current === undefined
        ) {
          return "";
        }
        return view.cost.current <= view.max_budget.current
          ? "positive-value"
          : "negative-value";
      case "pna":
        if (constraints.min_pna == null) return "";
        return view.pna_availability >= constraints.min_pna
          ? "positive-value"
          : "negative-value";
      case "shelfInventory":
        if (constraints.max_shelf_inventory_value_growth == null) return "";
        return view.shelf_inventory_value_growth <=
          constraints.max_shelf_inventory_value_growth
          ? "positive-value"
          : "negative-value";
      case "serviceLevel":
      default:
        return "";
    }
  };

  const handleChartClick = (e, type, name, title) => {
    if (
      e &&
      e.target &&
      (e.target.closest(".modebar") || e.target.closest(".modebar-btn"))
    ) {
      return;
    }
    setPopupChart({ type, name, title });
  };

  const closePopup = () => setPopupChart(null);

  const handleSkuPageChange = (page) => {
    // La UI ci passa una pagina 1-based, noi usiamo 0-based
    const pageN = page - 1;

    // Aggiorniamo solo lo stato. L'useEffect farà il resto.
    setSkuCurrentPage(pageN);

    document
      .querySelector(".sku-overview-section")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  // Aggiorna un singolo campo di una riga di filtro
  const handleFilterRowChange = (id, field, newValue) => {
    setFamilyFilterInputs((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: newValue } : row)),
    );
  };

  // Aggiungi una nuova riga vuota
  const addFilterRow = () => {
    setFamilyFilterInputs((prev) => [
      ...prev,
      { id: Date.now(), column: "roi", operator: ">", value: "" },
    ]);
  };

  // Rimuovi una riga e APPLICA IMMEDIATAMENTE i filtri rimanenti
  const removeFilterRow = (id) => {
    const newInputs = familyFilterInputs.filter((row) => row.id !== id);
    setFamilyFilterInputs(newInputs);

    // Ricalcola e applica subito i filtri basandosi sulle righe rimaste
    applyFiltersInternal(newInputs);
  };

  // Logica di applicazione filtri (estratta per essere riutilizzabile)
  const applyFiltersInternal = (inputs) => {
    const filters = {};

    inputs.forEach(({ column, operator, value }) => {
      const cleanedValue = String(value).replace(/[%$]/g, "").replace(/,/g, "");
      if (cleanedValue.trim() !== "" && !isNaN(parseFloat(cleanedValue))) {
        const numericValue = parseFloat(cleanedValue);
        if (operator === ">") {
          filters[`${column}_min`] = numericValue;
        } else if (operator === "<") {
          filters[`${column}_max`] = numericValue;
        }
      }
    });

    setAppliedFamilyFilters(filters);
  };

  // Wrapper per il bottone "Apply Filter" che usa lo stato corrente
  const handleApplyFamilyFilters = () => {
    applyFiltersInternal(familyFilterInputs);
  };

  const resetFamilyFilters = () => {
    setFamilyFilterInputs([
      {
        id: Date.now(),
        column: "roi",
        operator: ">",
        value: "",
      },
    ]);

    // Rimuove i filtri applicati (ricarica la tabella con tutti i dati)
    setAppliedFamilyFilters({});
  };

  // gestisce l'applicazione dei filtri
  const handleSkuFilterRowChange = (id, field, newValue) => {
    // Aggiornamento standard dello stato visivo
    setSkuFilterInputs((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: newValue } : row)),
    );

    // LOGICA SPECIFICA PER SKU_ID
    // Troviamo la riga corrente per sapere su che colonna stiamo lavorando
    const currentRow = skuFilterInputs.find((r) => r.id === id);
    const isSkuColumn =
      (field === "column" && newValue === "sku_id") ||
      (currentRow && currentRow.column === "sku_id");

    // Se stiamo digitando nel campo "value" e la colonna è "sku_id"
    if (isSkuColumn && field === "value") {
      // Pulisci il timer precedente
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }

      // Se il campo è vuoto, pulisci suggerimenti e non cercare
      if (!newValue || newValue.trim() === "") {
        setSkuSuggestions([]);
        return;
      }

      setIsSearchingSku(true); // Mostra spinner
      setActiveSuggestionRowId(id);

      searchDebounceRef.current = setTimeout(async () => {
        try {
          const response = await fetchSimilarSkus(newValue);
          if (response && response.skus) {
            setSkuSuggestions(response.skus);
          } else {
            setSkuSuggestions([]);
          }
        } catch (err) {
          console.error("Error searching SKUs", err);
          setSkuSuggestions([]);
        } finally {
          setIsSearchingSku(false);
        }
      }, 750);
    } else {
      if (field === "column" && newValue !== "sku_id") {
        setSkuSuggestions([]);
        setActiveSuggestionRowId(null);
      }
    }
  };

  // Aggiunge riga
  const addSkuFilterRow = () => {
    setSkuFilterInputs((prev) => [
      ...prev,
      { id: Date.now(), column: "roi", operator: ">", value: "" },
    ]);
  };

  // Rimuove riga e applica
  const removeSkuFilterRow = (id) => {
    const newInputs = skuFilterInputs.filter((row) => row.id !== id);
    setSkuFilterInputs(newInputs);
    applySkuFiltersInternal(newInputs);
  };

  // Logica interna di applicazione
  const applySkuFiltersInternal = (inputs) => {
    const filters = {};

    inputs.forEach(({ column, operator, value }) => {
      // Se la colonna è sku_id, passala direttamente
      if (column === "sku_id") {
        if (value && value.trim() !== "") {
          filters["sku_id"] = value.trim();
        }
      } else {
        const cleanedValue = String(value)
          .replace(/[%$]/g, "")
          .replace(/,/g, "");
        if (cleanedValue.trim() !== "" && !isNaN(parseFloat(cleanedValue))) {
          const numericValue = parseFloat(cleanedValue);
          if (operator === ">") {
            filters[`${column}_min`] = numericValue;
          } else if (operator === "<") {
            filters[`${column}_max`] = numericValue;
          }
        }
      }
    });

    setSkuCurrentPage(0);
    setAppliedSkuFilters(filters);
  };

  // Bottone Apply
  const handleApplySkuFilters = () => {
    applySkuFiltersInternal(skuFilterInputs);
  };

  // Bottone Reset
  const resetSkuFilters = () => {
    setSkuFilterInputs([
      {
        id: Date.now(),
        column: "roi",
        operator: ">",
        value: "",
      },
    ]);
    setSkuCurrentPage(0);
    setAppliedSkuFilters({});
  };

  // Componente per renderizzare i controlli di paginazione
  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    // La prop currentPage è 0-based, ma la UI lavora con 1-based
    const currentPageUI = currentPage + 1;

    const renderControls = () => {
      if (totalPages <= 1) return null;

      const buttons = [];
      const maxVisibleButtons = 5;

      // Pulsante "Previous"
      buttons.push(
        <button
          key="prev"
          className="pagination-btn"
          onClick={() => onPageChange(currentPageUI - 1)}
          disabled={currentPageUI === 1}
        >
          Previous
        </button>,
      );

      // Logica per creare i numeri di pagina e gli "..."
      let startPage, endPage;
      if (totalPages <= maxVisibleButtons + 2) {
        startPage = 1;
        endPage = totalPages;
      } else {
        if (currentPageUI <= 3) {
          startPage = 1;
          endPage = 5;
        } else if (currentPageUI >= totalPages - 2) {
          startPage = totalPages - 4;
          endPage = totalPages;
        } else {
          startPage = currentPageUI - 2;
          endPage = currentPageUI + 2;
        }
      }

      if (startPage > 1) {
        buttons.push(
          <button
            key={1}
            className="pagination-btn"
            onClick={() => onPageChange(1)}
          >
            1
          </button>,
        );
        if (startPage > 2) {
          buttons.push(
            <span key="start-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        buttons.push(
          <button
            key={i}
            className={`pagination-btn ${currentPageUI === i ? "active" : ""}`}
            onClick={() => onPageChange(i)}
          >
            {i}
          </button>,
        );
      }

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          buttons.push(
            <span key="end-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
        buttons.push(
          <button
            key={totalPages}
            className="pagination-btn"
            onClick={() => onPageChange(totalPages)}
          >
            {totalPages}
          </button>,
        );
      }

      // Pulsante "Next"
      buttons.push(
        <button
          key="next"
          className="pagination-btn"
          onClick={() => onPageChange(currentPageUI + 1)}
          disabled={currentPageUI === totalPages}
        >
          Next
        </button>,
      );

      return buttons;
    };

    return <div className="pagination-container">{renderControls()}</div>;
  };

  if (loading)
    return (
      <div
        style={{
          color: "white",
          textAlign: "center",
          fontSize: "2rem",
          paddingTop: "5rem",
        }}
      >
        Loading Dashboard...
      </div>
    );
  if (error)
    return (
      <div
        style={{
          color: "red",
          textAlign: "center",
          fontSize: "2rem",
          paddingTop: "5rem",
        }}
      >
        Error: {error}
      </div>
    );

  const kpiConfig =
    config?.pageLayouts.global.kpiBoxes.filter((k) => k.visible) || [];
  const kpiDisplayData =
    globalView && globalConstraints
      ? {
          roi: {
            title: "ROI",
            value: formatPercentage(globalView.roi.current),
            limit: `(Min: ${formatPercentage(globalView.roi.min_roi)})`,
          },
          netMargin: {
            title: "NET MARGIN",
            value: formatCurrency(globalView.net_margin.current),
          },
          grossMargin: {
            title: "GROSS MARGIN",
            value: formatCurrency(globalView.gross_margin.current),
          },
          budget: {
            title: "TOTAL COST",
            value: formatCurrency(globalView.cost.current),
            limit: `(Max: ${
              globalView.max_budget.current != null
                ? formatCurrency(globalView.max_budget.current)
                : "N/A"
            })`,
          },
          serviceLevel: {
            title: "SERVICE LEVEL",
            value: formatPercentage(globalView.service_level.current),
          },
          pna: {
            title: "PNA AVAILABILITY",
            value: formatPercentage(globalView.pna_availability),
            limit: globalConstraints.min_pna
              ? `(Min: ${formatPercentage(globalConstraints.min_pna)})`
              : null,
          },
          shelfInventory: {
            title: "SHELF INVENTORY GROWTH",
            value: formatPercentage(globalView.shelf_inventory_value_growth),
            limit: globalConstraints.max_shelf_inventory_value_growth
              ? `(Max: ${formatPercentage(
                  globalConstraints.max_shelf_inventory_value_growth,
                )})`
              : null,
          },
        }
      : {};

  return (
    <>
      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}

      <div className={popupChart ? "blur-background" : ""}>
        <header>
          <h1>GLOBAL VIEW</h1>
          <div className="header-buttons">
            <Link to="/alerts" className="header-btn alerts-btn">
              {alertsLoading ? (
                  <>
                    ALERTS{" "}
                    <span
                      className="alerts-count-skeleton"
                      role="status"
                      aria-label="Loading alerts"
                    />
                  </>
                ) : (
                  `ALERTS (${alertsCount})`
                )}
            </Link>
            <Link to="/forecast?level=GLOBAL" className="header-btn">
              FORECAST
            </Link>
            <Link to="/master-data" className="header-btn">
              IMPORTED DATA
            </Link>

            {/* User menu */}
            <div className="user-menu-wrapper" ref={userMenuRef}>
              <button
                className="user-menu-btn"
                onClick={() => setIsUserMenuOpen((o) => !o)}
                aria-label="User menu"
                aria-expanded={isUserMenuOpen}
                aria-haspopup="true"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
                </svg>
              </button>

              {isUserMenuOpen && (
                <div className="user-menu-dropdown" role="menu">
                  <button
                    className="user-menu-item"
                    role="menuitem"
                    onClick={() => { setIsUserMenuOpen(false); setShowChangePassword(true); }}
                  >
                    Change password
                  </button>
                  <button
                    className="user-menu-item user-menu-item--danger"
                    role="menuitem"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="dashboard-container">
          {config && globalView && (
            <section className="overview-section">
              <h2 className="section-title-container">
                EXPECTED VALUE & HISTORY
              </h2>
              <div className="kpi-grid-row-top">
                {kpiConfig.slice(0, 3).map(({ id }) => {
                  const kpi = kpiDisplayData[id];
                  if (!kpi) return null;
                  const chartConfig = getKpiChartConfig(
                    id,
                    kpiHistory,
                    globalConstraints,
                    false,
                    config,
                    unconstrainedKpiHistory,
                  );
                  return (
                    <div className="kpi-box" data-kpi-id={id} key={id}>
                      <div className="kpi-title">
                        <InfoIcon label={kpi.title} />
                      </div>
                      <div className={`kpi-value ${getKpiColorClass(id)}`}>
                        <span>
                          Current: <strong>{kpi.value}</strong>
                        </span>
                        {kpi.limit && (
                          <span className="kpi-min-value">{kpi.limit}</span>
                        )}
                      </div>
                      <div
                        className="graph-placeholder"
                        onClick={(e) =>
                          handleChartClick(
                            e,
                            "kpi",
                            id,
                            `${kpi.title} EVOLUTION`,
                          )
                        }
                      >
                        <Plot
                          data={chartConfig.traces}
                          layout={chartConfig.layout}
                          config={chartConfig.config}
                          style={{ width: "100%", height: "100%" }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="kpi-grid-row-bottom">
                {kpiConfig.slice(3, 7).map(({ id }) => {
                  const kpi = kpiDisplayData[id];
                  if (!kpi) return null;
                  const chartConfig = getKpiChartConfig(
                    id,
                    kpiHistory,
                    globalConstraints,
                    false,
                    config,
                    unconstrainedKpiHistory,
                  );
                  return (
                    <div className="kpi-box" data-kpi-id={id} key={id}>
                      <div className="kpi-title">
                        <InfoIcon label={kpi.title} />
                      </div>
                      <div className={`kpi-value ${getKpiColorClass(id)}`}>
                        <span>
                          Current: <strong>{kpi.value}</strong>
                        </span>
                        {kpi.limit && (
                          <span className="kpi-min-value">{kpi.limit}</span>
                        )}
                      </div>
                      <div
                        className="graph-placeholder"
                        onClick={(e) =>
                          handleChartClick(
                            e,
                            "kpi",
                            id,
                            `${kpi.title} EVOLUTION`,
                          )
                        }
                      >
                        <Plot
                          data={chartConfig.traces}
                          layout={chartConfig.layout}
                          config={chartConfig.config}
                          style={{ width: "100%", height: "100%" }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <div className="data-grid">
            {config && globalConstraints && (
              <div className="split-box">
                <h2>GLOBAL CONSTRAINTS</h2>
                <div className="split-box-content">
                  {config.pageLayouts.global.constraintItems
                    .filter((c) => c.visible && c.id !== "minServiceLevel")
                    .map(({ id }) => {
                      // Conversione ID (es. maxBudget -> max_budget)
                      const apiKey = id.replace(
                        /[A-Z]/g,
                        (letter) => `_${letter.toLowerCase()}`,
                      );

                      const label =
                        constraintLabelMap[id] ||
                        id
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase())
                          .replace(/^Min\s/, "Minimum ")
                          .replace(/^Max\s/, "Maximum ")
                          .replace(/\bRoi\b/g, "ROI")
                          .replace(/\bPna\b/g, "PNA");

                      const value = globalConstraints[apiKey];

                      return (
                        <div className="split-item" key={id}>
                          <span>
                            <InfoIcon label={label} />
                          </span>
                          <span>
                            {(() => {
                              if (value === null || value === undefined) {
                                return "N/A";
                              }
                              const percentageFields = [
                                "min_service_level",
                                "min_roi",
                                "max_shelf_stockout_rate",
                                "max_shelf_inventory_value_growth",
                                "max_perc_unall_sku",
                                "min_pna",
                              ];

                              if (typeof value === "number") {
                                if (percentageFields.includes(apiKey)) {
                                  return formatPercentage(value);
                                }
                                return formatCurrency(value);
                              }
                              return value;
                            })()}
                          </span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* CONTENITORE PER I GRAFICI A TORTA */}
            {config && (
              <div className="pie-charts-container">
                <div
                  className="chart-container pie-chart-clickable"
                  onClick={(e) =>
                    handleChartClick(e, "pie", "margin", "Margin % Per Family")
                  }
                >
                  <h3>Margin % Per Family</h3>
                  <Plot
                    data={
                      getPieChartConfig("margin", pieChartData, false, config)
                        .traces
                    }
                    layout={
                      getPieChartConfig("margin", pieChartData, false, config)
                        .layout
                    }
                    config={
                      getPieChartConfig("margin", pieChartData, false, config)
                        .config
                    }
                  />
                </div>
                <div
                  className="chart-container pie-chart-clickable"
                  onClick={(e) =>
                    handleChartClick(
                      e,
                      "pie",
                      "cost",
                      "Total Cost % Per Family",
                    )
                  }
                >
                  {/* Titolo corretto */}
                  <h3>Total Cost % Per Family</h3>
                  <Plot
                    data={
                      getPieChartConfig("cost", pieChartData, false, config)
                        .traces
                    }
                    layout={
                      getPieChartConfig("cost", pieChartData, false, config)
                        .layout
                    }
                    config={
                      getPieChartConfig("cost", pieChartData, false, config)
                        .config
                    }
                  />
                </div>
              </div>
            )}
          </div>

          <section className="family-overview-section">
            <h2 className="section-title-container">OVERVIEW BY FAMILY</h2>
            <div className="constraint-controls">
              <div className="constraint-tabs">
                <button
                  className={`constraint-tab ${familyViewMode === "constrained" ? "active" : ""}`}
                  onClick={() => setFamilyViewMode("constrained")}
                >
                  Constrained
                </button>
                <button
                  className={`constraint-tab ${familyViewMode === "unconstrained" ? "active" : ""}`}
                  onClick={() => setFamilyViewMode("unconstrained")}
                >
                  Unconstrained
                </button>
              </div>
              <label
                className="compare-switch"
                title={
                  familyViewMode === "constrained"
                    ? "Compare Constrained → Unconstrained"
                    : "Compare Unconstrained → Constrained"
                }
              >
                <input
                  type="checkbox"
                  checked={isFamilyCompareActive}
                  disabled={!hasFamilyUnconstrained}
                  onChange={() =>
                    setIsFamilyCompareActive((prev) => !prev)
                  }
                />
                <span className="compare-slider" />
                <span className="compare-switch-label">Compare</span>
              </label>
            </div>
            <div className="filter-section">
              {familyFilterInputs.map((row, index) => {
                const isLast = index === familyFilterInputs.length - 1;
                const showMinus = familyFilterInputs.length > 1;

                return (
                  <div key={row.id} className="filter-row">
                    <span className="filter-label">
                      {index === 0 ? "Filter by" : "AND"}
                    </span>

                    {/* Select Column */}
                    <select
                      value={row.column}
                      onChange={(e) =>
                        handleFilterRowChange(row.id, "column", e.target.value)
                      }
                    >
                      <option value="roi">ROI</option>
                      <option value="sl">SERVICE LEVEL</option>
                      <option value="net_margin">NET MARGIN</option>
                      <option value="gross_margin">GROSS MARGIN</option>
                      <option value="cost">BUDGET</option>
                      <option value="pna_availability">PNA AVAILABILITY</option>
                    </select>

                    {/* Select Operator */}
                    <select
                      value={row.operator}
                      onChange={(e) =>
                        handleFilterRowChange(
                          row.id,
                          "operator",
                          e.target.value,
                        )
                      }
                    >
                      <option value=">">&gt;</option>
                      <option value="<">&lt;</option>
                    </select>

                    {/* Input Value */}
                    <input
                      type="text"
                      placeholder="Enter value..."
                      value={row.value}
                      onChange={(e) =>
                        handleFilterRowChange(row.id, "value", e.target.value)
                      }
                    />

                    {/* ACTION BUTTONS */}
                    <div className="filter-actions">
                      {/* Minus Button */}
                      {showMinus && (
                        <button
                          onClick={() => removeFilterRow(row.id)}
                          className="icon-action-btn"
                          title="Remove filter"
                        >
                          {iconMinus}
                        </button>
                      )}

                      {/* Plus Button */}
                      <button
                        onClick={addFilterRow}
                        className="icon-action-btn"
                        title="Add another filter"
                      >
                        {iconPlus}
                      </button>

                      {/* Action Buttons (Apply, Reset, Download) */}
                      {isLast && (
                        <>
                          <button
                            onClick={handleApplyFamilyFilters}
                            className="filter-btn"
                            style={{ marginLeft: "10px" }}
                          >
                            Apply Filter
                          </button>
                          <button
                            onClick={resetFamilyFilters}
                            className="filter-btn"
                            style={{ marginLeft: "10px" }}
                          >
                            Reset
                          </button>

                          <div
                            className="split-btn-container"
                            style={{ marginLeft: "auto" }}
                          >
                            <button
                              onClick={handleDownloadFamily}
                              className={`split-main-btn ${
                                isDownloadingFamily ? "loading" : ""
                              }`}
                              disabled={isDownloadingFamily}
                              title="Download XLSX"
                            >
                              <div className="btn-icon">{iconDownload}</div>
                              <span>Download</span>
                            </button>
                            <button
                              className="split-arrow-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsFamilyDownloadDropdownOpen(
                                  !isFamilyDownloadDropdownOpen,
                                );
                              }}
                            >
                              <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"
                                style={{ transform: isFamilyDownloadDropdownOpen ? "rotate(-180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
                                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                            {isFamilyDownloadDropdownOpen && (
                              <div className="split-dropdown-menu">
                                <button
                                  onClick={handleDownloadFamilyUnfiltered}
                                  className={`split-dropdown-item ${isDownloadingFamily ? "loading" : ""}`}
                                  disabled={isDownloadingFamily}
                                >
                                  <div className="btn-icon" style={{ width: "16px", height: "16px" }}>{iconDownload}</div>
                                  <span>Download without filters</span>
                                </button>
                              </div>
                            )}
                          </div>

                          {/* --- BOTTONE COLONNE FAMILY --- */}
                          <div
                            className="column-visibility-container"
                            style={{ marginLeft: "10px" }}
                            ref={familyColsRef}
                          >
                            <button
                              className="toggle-btn column-toggle-btn"
                              onClick={() =>
                                setIsFamilyColumnDropdownOpen(
                                  !isFamilyColumnDropdownOpen,
                                )
                              }
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                fill="#f5f5f5"
                                viewBox="0 0 256 256"
                              >
                                <path d="M84,52V92H28a4,4,0,0,1-4-4V64A16,16,0,0,1,40,48H80A4,4,0,0,1,84,52Zm16,152a4,4,0,0,0,4,4h48a4,4,0,0,0,4-4V164H100ZM24,168v24a16,16,0,0,0,16,16H80a4,4,0,0,0,4-4V164H28A4,4,0,0,0,24,168Zm0-56v32a4,4,0,0,0,4,4H84V108H28A4,4,0,0,0,24,112ZM152,48H104a4,4,0,0,0-4,4V92h56V52A4,4,0,0,0,152,48Zm76,60H172v40h56a4,4,0,0,0,4-4V112A4,4,0,0,0,228,108ZM100,148h56V108H100ZM216,48H176a4,4,0,0,0-4,4V92h56a4,4,0,0,0,4-4V64A16,16,0,0,0,216,48Zm12,116H172v40a4,4,0,0,0,4,4h40a16,16,0,0,0,16-16V168A4,4,0,0,0,228,164Z"></path>
                              </svg>
                              <span>COLUMNS</span>
                            </button>
                            {isFamilyColumnDropdownOpen && (
                              <div className="column-dropdown show">
                                {familyColumnOrder.map((colName, index) => (
                                  <div
                                    key={colName}
                                    className="column-dropdown-item"
                                    draggable="true"
                                    onDragStart={(e) => {
                                      e.dataTransfer.setData(
                                        "draggedIdx",
                                        index,
                                      );
                                      e.currentTarget.classList.add("dragging");
                                    }}
                                    onDragEnd={(e) => {
                                      e.currentTarget.classList.remove(
                                        "dragging",
                                      );
                                    }}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.currentTarget.classList.add(
                                        "drag-over",
                                      );
                                    }}
                                    onDragLeave={(e) => {
                                      e.currentTarget.classList.remove(
                                        "drag-over",
                                      );
                                    }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      e.currentTarget.classList.remove(
                                        "drag-over",
                                      );
                                      const draggedIdx =
                                        e.dataTransfer.getData("draggedIdx");
                                      handleFamilyOnDrop(
                                        parseInt(draggedIdx),
                                        index,
                                      );
                                    }}
                                    style={{ cursor: "grab" }}
                                  >
                                    <span style={{ flex: 1 }}>{colName}</span>

                                    <div
                                      style={{ display: "flex", gap: "5px" }}
                                    >
                                      {/* Pulsante VISIBILITÀ */}
                                      <div
                                        className="column-visibility-icon"
                                        onClick={() =>
                                          setFamilyColumnVisibility((prev) => ({
                                            ...prev,
                                            [colName]: !prev[colName],
                                          }))
                                        }
                                        style={{
                                          cursor: "pointer",
                                        }}
                                      >
                                        {familyColumnVisibility[colName]
                                          ? iconVisible
                                          : iconHidden}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="main-table-container">
              <table className="main-table">
                <thead>
                  <tr>
                    <SortableHeader
                      label="Family"
                      sortKey="family"
                      onSort={requestSort}
                      sortIndicator={getSortIndicator("family")}
                    />

                    <SortableHeader
                      label="Family Description"
                      sortKey="family_description"
                      onSort={requestSort}
                      sortIndicator={getSortIndicator("family_description")}
                    />

                    {familyColumnOrder.map((colName) => {
                      if (!familyColumnVisibility[colName]) return null;

                      const def = FAMILY_COLUMN_DEFS[colName];
                      if (!def) return null;

                      return (
                        <SortableHeader
                          key={colName}
                          label={colName}
                          sortKey={def.sortKey}
                          onSort={requestSort}
                          sortIndicator={getSortIndicator(def.sortKey)}
                        />
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {isFamilyLoading ? (
                    <tr>
                      <td
                        colSpan="10"
                        style={{ textAlign: "center", padding: "2rem" }}
                      >
                        Loading family data...
                      </td>
                    </tr>
                  ) : familyViewMode === "unconstrained" && !hasFamilyUnconstrained ? (
                    <tr>
                      <td
                        colSpan={2 + familyColumnOrder.filter((c) => familyColumnVisibility[c]).length}
                        style={{ textAlign: "center", padding: "2rem", color: "#8899aa" }}
                      >
                        Unconstrained data not yet available
                      </td>
                    </tr>
                  ) : (
                    sortedFamilyData.map((row, idx) => {
                      const cRow = familyTableData.find(
                        (r) => r.family === row.family,
                      ) || row;
                      const uRow = unconstrainedFamilyMap.get(row.family);
                      const baseRow =
                        familyViewMode === "constrained" ? cRow : uRow;
                      const targetRow =
                        familyViewMode === "constrained" ? uRow : cRow;
                      return (
                        <tr key={`${row.family}-${idx}`}>
                          <td className="highlight-link">
                            <Link to={`/family/${row.family}`}>
                              {row.family}
                            </Link>
                          </td>

                          <td>{row.family_description}</td>

                          {familyColumnOrder.map((colName) => {
                            if (!familyColumnVisibility[colName]) return null;

                            const def = FAMILY_COLUMN_DEFS[colName];
                            if (!def) return null;

                            const cellClass = def.className
                              ? def.className(row)
                              : "";

                            return (
                              <td
                                key={colName}
                                className={`${cellClass}${isFamilyCompareActive ? " compare-mode-cell" : ""}`}
                              >
                                {isFamilyCompareActive
                                  ? renderCompareCell(
                                      def,
                                      baseRow,
                                      targetRow,
                                      formatPercentage,
                                      formatCurrency,
                                    )
                                  : def.render(
                                      row,
                                      formatPercentage,
                                      formatCurrency,
                                    )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="sku-overview-section">
            <h2 className="section-title-container">OVERVIEW BY SKU</h2>
            <div className="constraint-controls">
              <div className="constraint-tabs">
                <button
                  className={`constraint-tab ${skuViewMode === "constrained" ? "active" : ""}`}
                  onClick={() => setSkuViewMode("constrained")}
                >
                  Constrained
                </button>
                <button
                  className={`constraint-tab ${skuViewMode === "unconstrained" ? "active" : ""}`}
                  onClick={() => setSkuViewMode("unconstrained")}
                >
                  Unconstrained
                </button>
              </div>
              <label
                className="compare-switch"
                title={
                  skuViewMode === "constrained"
                    ? "Compare Constrained → Unconstrained"
                    : "Compare Unconstrained → Constrained"
                }
              >
                <input
                  type="checkbox"
                  checked={isSkuCompareActive}
                  disabled={!hasSkuUnconstrained}
                  onChange={() =>
                    setIsSkuCompareActive((prev) => !prev)
                  }
                />
                <span className="compare-slider" />
                <span className="compare-switch-label">Compare</span>
              </label>
            </div>
            <div className="filter-section">
              {skuFilterInputs.map((row, index) => {
                const isLast = index === skuFilterInputs.length - 1;
                const showMinus = skuFilterInputs.length > 1;

                return (
                  <div key={row.id} className="filter-row">
                    {/* Label */}
                    <span className="filter-label">
                      {index === 0 ? "Filter by" : "AND"}
                    </span>

                    {/* Select Column (Opzioni specifiche per SKU) */}
                    <select
                      value={row.column}
                      onChange={(e) =>
                        handleSkuFilterRowChange(
                          row.id,
                          "column",
                          e.target.value,
                        )
                      }
                    >
                      <option value="sku_id">SKU ID</option>
                      <option value="roi">ROI</option>
                      <option value="lq">LQ</option>
                      <option value="rq">ROQ</option>
                      <option value="service_level">SERVICE LEVEL</option>
                    </select>

                    <select
                      value={row.operator}
                      onChange={(e) =>
                        handleSkuFilterRowChange(
                          row.id,
                          "operator",
                          e.target.value,
                        )
                      }
                      disabled={row.column === "sku_id"} // Disabilita se SKU ID
                      style={row.column === "sku_id" ? { opacity: 0.5 } : {}}
                    >
                      <option value=">">&gt;</option>
                      <option value="<">&lt;</option>
                    </select>

                    {/* Input Wrapper per gestire il dropdown */}
                    <div className="filter-input-wrapper">
                      <input
                        type="text"
                        placeholder={
                          row.column === "sku_id"
                            ? "Type SKU..."
                            : "Enter value..."
                        }
                        value={row.value}
                        onChange={(e) =>
                          handleSkuFilterRowChange(
                            row.id,
                            "value",
                            e.target.value,
                          )
                        }
                        onBlur={() =>
                          setTimeout(() => setActiveSuggestionRowId(null), 200)
                        }
                      />

                      {/* Spinner caricamento */}
                      {isSearchingSku && activeSuggestionRowId === row.id && (
                        <div className="input-loading-spinner"></div>
                      )}

                      {/* Dropdown Suggerimenti */}
                      {activeSuggestionRowId === row.id &&
                        skuSuggestions.length > 0 &&
                        row.column === "sku_id" && (
                          <div className="suggestions-dropdown">
                            {skuSuggestions.map((item, idx) => (
                              <div
                                key={idx}
                                className="suggestion-item"
                                onMouseDown={() =>
                                  handleSelectSkuSuggestion(row.id, item.sku_id)
                                } // onMouseDown scatta prima di onBlur
                              >
                                {item.sku_id}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>

                    {/* ACTION BUTTONS */}
                    <div className="filter-actions">
                      {" "}
                      {/* Minus Button */}
                      {showMinus && (
                        <button
                          onClick={() => removeSkuFilterRow(row.id)}
                          className="icon-action-btn"
                          title="Remove filter"
                        >
                          {iconMinus}
                        </button>
                      )}
                      {/* Plus Button */}
                      <button
                        onClick={addSkuFilterRow}
                        className="icon-action-btn"
                        title="Add another filter"
                      >
                        {iconPlus}
                      </button>
                      {/* Apply & Reset Buttons */}
                      {isLast && (
                        <>
                          <button
                            onClick={handleApplySkuFilters}
                            className="filter-btn"
                            style={{ marginLeft: "10px" }}
                          >
                            Apply Filter
                          </button>
                          <button
                            onClick={resetSkuFilters}
                            className="filter-btn"
                            style={{ marginLeft: "10px" }}
                          >
                            Reset
                          </button>
                          <div
                            className="split-btn-container"
                            style={{ marginLeft: "auto" }}
                          >
                            <button
                              onClick={handleDownloadSku}
                              className={`split-main-btn ${
                                isDownloadingSku ? "loading" : ""
                              }`}
                              disabled={isDownloadingSku}
                              title="Download XLSX"
                            >
                              <div className="btn-icon">{iconDownload}</div>
                              <span>Download</span>
                            </button>
                            <button
                              className="split-arrow-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsSkuDownloadDropdownOpen(
                                  !isSkuDownloadDropdownOpen,
                                );
                              }}
                            >
                              <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"
                                style={{ transform: isSkuDownloadDropdownOpen ? "rotate(-180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
                                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                            {isSkuDownloadDropdownOpen && (
                              <div className="split-dropdown-menu">
                                <button
                                  onClick={handleDownloadSkuUnfiltered}
                                  className={`split-dropdown-item ${isDownloadingSku ? "loading" : ""}`}
                                  disabled={isDownloadingSku}
                                >
                                  <div className="btn-icon" style={{ width: "16px", height: "16px" }}>{iconDownload}</div>
                                  <span>Download without filters</span>
                                </button>
                              </div>
                            )}
                          </div>

                          {/* --- BOTTONE COLONNE SKU --- */}
                          <div
                            className="column-visibility-container"
                            style={{ marginLeft: "10px" }}
                            ref={skuColsRef}
                          >
                            <button
                              className="toggle-btn column-toggle-btn"
                              onClick={() =>
                                setIsSkuColumnDropdownOpen(
                                  !isSkuColumnDropdownOpen,
                                )
                              }
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                fill="#f5f5f5"
                                viewBox="0 0 256 256"
                              >
                                <path d="M84,52V92H28a4,4,0,0,1-4-4V64A16,16,0,0,1,40,48H80A4,4,0,0,1,84,52Zm16,152a4,4,0,0,0,4,4h48a4,4,0,0,0,4-4V164H100ZM24,168v24a16,16,0,0,0,16,16H80a4,4,0,0,0,4-4V164H28A4,4,0,0,0,24,168Zm0-56v32a4,4,0,0,0,4,4H84V108H28A4,4,0,0,0,24,112ZM152,48H104a4,4,0,0,0-4,4V92h56V52A4,4,0,0,0,152,48Zm76,60H172v40h56a4,4,0,0,0,4-4V112A4,4,0,0,0,228,108ZM100,148h56V108H100ZM216,48H176a4,4,0,0,0-4,4V92h56a4,4,0,0,0,4-4V64A16,16,0,0,0,216,48Zm12,116H172v40a4,4,0,0,0,4,4h40a16,16,0,0,0,16-16V168A4,4,0,0,0,228,164Z"></path>
                              </svg>
                              <span>COLUMNS</span>
                            </button>
                            {isSkuColumnDropdownOpen && (
                              <div className="column-dropdown show">
                                {skuColumnOrder.map((colName, index) => (
                                  <div
                                    key={colName}
                                    className="column-dropdown-item"
                                    draggable="true"
                                    onDragStart={(e) => {
                                      e.dataTransfer.setData(
                                        "draggedIdx",
                                        index,
                                      );
                                      e.currentTarget.classList.add("dragging");
                                    }}
                                    onDragEnd={(e) => {
                                      e.currentTarget.classList.remove(
                                        "dragging",
                                      );
                                    }}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.currentTarget.classList.add(
                                        "drag-over",
                                      );
                                    }}
                                    onDragLeave={(e) => {
                                      e.currentTarget.classList.remove(
                                        "drag-over",
                                      );
                                    }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      e.currentTarget.classList.remove(
                                        "drag-over",
                                      );
                                      const draggedIdx =
                                        e.dataTransfer.getData("draggedIdx");
                                      handleSkuOnDrop(
                                        parseInt(draggedIdx),
                                        index,
                                      );
                                    }}
                                    style={{ cursor: "grab" }}
                                  >
                                    <span style={{ flex: 1 }}>{colName}</span>

                                    <div
                                      style={{ display: "flex", gap: "5px" }}
                                    >
                                      {/* Pulsante VISIBILITÀ */}
                                      <div
                                        className="column-visibility-icon"
                                        onClick={() =>
                                          setSkuColumnVisibility((prev) => ({
                                            ...prev,
                                            [colName]: !prev[colName],
                                          }))
                                        }
                                        style={{
                                          cursor: "pointer",
                                        }}
                                      >
                                        {skuColumnVisibility[colName]
                                          ? iconVisible
                                          : iconHidden}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="main-table-container full-page-table-sku">
              <table className="main-table">
                <thead>
                  <tr>
                    {/* COLONNE FISSE */}
                    <SortableHeader
                      label="SKU ID"
                      sortKey="sku_id"
                      onSort={requestSkuSort}
                      sortIndicator={getSkuSortIndicator("sku_id")}
                    />
                    <SortableHeader
                      label="SKU desc."
                      sortKey="sku_description"
                      onSort={requestSkuSort}
                      sortIndicator={getSkuSortIndicator("sku_description")}
                    />

                    {/* COLONNE DINAMICHE */}
                    {skuColumnOrder.map((colName) => {
                      if (!skuColumnVisibility[colName]) return null;

                      const def = SKU_COLUMN_DEFS[colName];
                      if (!def) return null;

                      return (
                        <SortableHeader
                          key={colName}
                          label={colName}
                          sortKey={def.sortKey}
                          onSort={requestSkuSort}
                          sortIndicator={getSkuSortIndicator(def.sortKey)}
                        />
                      );
                    })}
                  </tr>
                </thead>

                <tbody
                  style={{
                    opacity: isSkuLoading ? 0.5 : 1,
                    transition: "opacity 0.2s ease-in-out",
                  }}
                >
                  {isSkuLoading && skuTableData.length === 0 ? (
                    <tr>
                      <td
                        colSpan="16"
                        style={{ textAlign: "center", padding: "2rem" }}
                      >
                        Loading SKU data...
                      </td>
                    </tr>
                  ) : skuViewMode === "unconstrained" && !hasSkuUnconstrained ? (
                    <tr>
                      <td
                        colSpan={2 + skuColumnOrder.filter((c) => skuColumnVisibility[c]).length}
                        style={{ textAlign: "center", padding: "2rem", color: "#8899aa" }}
                      >
                        Unconstrained data not yet available
                      </td>
                    </tr>
                  ) : (
                    effectiveSkuData.map((row, idx) => {
                      const cRow = skuTableData.find(
                        (r) => r.sku_id === row.sku_id,
                      );
                      const uRow = unconstrainedSkuMap.get(row.sku_id);
                      const baseRow =
                        (skuViewMode === "constrained" ? cRow : uRow) || row;
                      const targetRow =
                        skuViewMode === "constrained" ? uRow : cRow;
                      return (
                        <tr key={`${row.sku_id}-${idx}`}>
                          <td className="highlight-link">
                            <Link
                              to={`/sku/${encodeURIComponent(
                                row.sku_id,
                              )}?from=global`}
                            >
                              {row.sku_id}
                            </Link>
                          </td>
                          <td>{row.sku_description}</td>

                          {skuColumnOrder.map((colName) => {
                            if (!skuColumnVisibility[colName]) return null;

                            const def = SKU_COLUMN_DEFS[colName];
                            if (!def) return <td key={colName}>Error</td>;

                            const cellClass = def.className
                              ? def.className(row)
                              : "";

                            return (
                              <td
                                key={colName}
                                className={`${cellClass}${isSkuCompareActive ? " compare-mode-cell" : ""}`}
                              >
                                {isSkuCompareActive
                                  ? renderCompareCell(
                                      def,
                                      baseRow,
                                      targetRow,
                                      formatPercentage,
                                      formatCurrency,
                                      formatNumber,
                                      formatUnitCurrency,
                                    )
                                  : def.render(
                                      row,
                                      formatPercentage,
                                      formatCurrency,
                                      formatNumber,
                                      formatUnitCurrency,
                                    )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={skuCurrentPage}
              totalPages={skuTotalPages}
              onPageChange={handleSkuPageChange}
            />
          </section>

          <section className="forecast-section" ref={forecastSectionRef}>
            <h2 className="section-title-container">FORECAST</h2>
            <div className="forecast-mode-tabs">
              <button
                className={`forecast-mode-tab ${forecastMode === "demand" ? "active" : ""}`}
                onClick={() => { setForecastMode("demand"); setIsCompareActive(false); setForecastSortConfig({ key: null, direction: null }); }}
              >
                Demand
              </button>
              <button
                className={`forecast-mode-tab ${forecastMode === "winrate" ? "active" : ""}`}
                onClick={() => { setForecastMode("winrate"); setIsCompareActive(false); setForecastSortConfig({ key: null, direction: null }); }}
              >
                Win Rate
              </button>
            </div>
            <div className="filter-section forecast-controls">
              <span>Generation</span>
              <select
                id="generation-filter"
                value={forecastMode === "demand" ? selectedGenerationIndex : selectedWinrateGenIndex}
                onChange={(e) => {
                  const newIndex = parseInt(e.target.value, 10);
                  if (forecastMode === "demand") {
                    setSelectedGenerationIndex(newIndex);
                    if (newIndex === 0) setIsCompareActive(false);
                    if (availableRuns[newIndex]) {
                      const targetRunId = availableRuns[newIndex].run_id;
                      setForecastData(allForecastData.filter((row) => row.run_id === targetRunId));
                    } else {
                      setForecastData([]);
                    }
                  } else {
                    setSelectedWinrateGenIndex(newIndex);
                    if (newIndex === 0) setIsCompareActive(false);
                    if (winrateRuns[newIndex]) {
                      const targetRunId = winrateRuns[newIndex].run_id;
                      setWinrateData(allWinrateData.filter((row) => row.run_id === targetRunId));
                    } else {
                      setWinrateData([]);
                    }
                  }
                  forecastSectionRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }}
              >
                {(forecastMode === "demand" ? availableRuns : winrateRuns).map((run, index) => {
                  const dateObj = new Date(run.run_date);
                  const label = `${formatPeriod(dateObj.toISOString().slice(0, 10))}, ${dateObj.toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                    timeZone: "UTC",
                  })}`;
                  return (
                    <option key={run.run_id} value={index}>
                      {label}
                    </option>
                  );
                })}
                {(forecastMode === "demand" ? availableRuns : winrateRuns).length === 0 && (
                  <option value={0}>No Data Available</option>
                )}
              </select>

              <span>Customer</span>
              <select
                value={customerType}
                onChange={(e) => setCustomerType(e.target.value)}
              >
                <option value="combined">Combined</option>
                <option value="prime">Prime</option>
                <option value="reseller">Reseller</option>
              </select>

              {forecastMode === "winrate" && (
                <>
                  <span>Type</span>
                  <div className="availability-group">
                    <select
                      value={availabilityType}
                      onChange={(e) => setAvailabilityType(e.target.value)}
                    >
                      <option value="available">Available</option>
                      <option value="unavailable">Unavailable</option>
                    </select>
                    <label
                      className="compare-switch"
                      title={
                        availabilityType === "available"
                          ? "Compare Available → Unavailable"
                          : "Compare Unavailable → Available"
                      }
                    >
                      <input
                        type="checkbox"
                        checked={isWinrateCompareActive}
                        onChange={() => setIsWinrateCompareActive((prev) => !prev)}
                      />
                      <span className="compare-slider" />
                      <span className="compare-switch-label">
                        vs {availabilityType === "available" ? "Unavailable" : "Available"}
                      </span>
                    </label>
                  </div>
                </>
              )}

              <div className="compare-btn-wrapper">
                <button
                  id="compare-toggle-btn"
                  className={`toggle-btn ${isCompareActive ? "active" : ""}`}
                  disabled={forecastMode === "demand" ? selectedGenerationIndex === 0 : selectedWinrateGenIndex === 0}
                  onClick={() => setIsCompareActive(!isCompareActive)}
                >
                  Compare to Current
                </button>
              </div>

              <div className="toggle-container">
                {forecastMode === "demand" ? (
                  <div
                    className="split-btn-container"
                    style={{ marginRight: "10px" }}
                  >
                    <button
                      onClick={handleDownloadForecast}
                      className={`split-main-btn ${isDownloadingForecast ? "loading" : ""}`}
                      disabled={isDownloadingForecast}
                      title="Download Global Forecast"
                    >
                      <div className="btn-icon">{iconDownload}</div>
                      <span>Download</span>
                    </button>
                    <button
                      className="split-arrow-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsDownloadDropdownOpen(!isDownloadDropdownOpen);
                      }}
                    >
                      <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"
                        style={{ transform: isDownloadDropdownOpen ? "rotate(-180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
                        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    {isDownloadDropdownOpen && (
                      <div className="split-dropdown-menu">
                        <button
                          onClick={handleDownloadForecastUnfiltered}
                          className={`split-dropdown-item ${isDownloadingForecast ? "loading" : ""}`}
                          disabled={isDownloadingForecast || availableRuns.length === 0}
                        >
                          <div className="btn-icon" style={{ width: "16px", height: "16px" }}>{iconDownload}</div>
                          <span>Download without filters</span>
                        </button>
                        <button
                          onClick={() => handleDownloadAllSkus()}
                          className={`split-dropdown-item ${isDownloadingAllSkus ? "loading" : ""}`}
                          disabled={isDownloadingAllSkus || availableRuns.length === 0}
                        >
                          <div className="btn-icon" style={{ width: "16px", height: "16px" }}>{iconDownload}</div>
                          <span>Download all SKUs without filters</span>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className="split-btn-container"
                    style={{ marginRight: "10px" }}
                  >
                    <button
                      onClick={handleDownloadWinrate}
                      className={`split-main-btn ${isDownloadingWinrate ? "loading" : ""}`}
                      disabled={isDownloadingWinrate}
                      title="Download Win Rate Forecast"
                    >
                      <div className="btn-icon">{iconDownload}</div>
                      <span>Download</span>
                    </button>
                    <button
                      className="split-arrow-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsWinrateDropdownOpen(!isWinrateDropdownOpen);
                      }}
                    >
                      <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"
                        style={{ transform: isWinrateDropdownOpen ? "rotate(-180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
                        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    {isWinrateDropdownOpen && (
                      <div className="split-dropdown-menu">
                        <button
                          onClick={handleDownloadWinrateUnfiltered}
                          className={`split-dropdown-item ${isDownloadingWinrate ? "loading" : ""}`}
                          disabled={isDownloadingWinrate || winrateRuns.length === 0}
                        >
                          <div className="btn-icon" style={{ width: "16px", height: "16px" }}>{iconDownload}</div>
                          <span>Download without filters</span>
                        </button>
                        <button
                          onClick={() => handleDownloadAllSkusWinrate()}
                          className={`split-dropdown-item ${isDownloadingAllSkusWinrate ? "loading" : ""}`}
                          disabled={isDownloadingAllSkusWinrate || winrateRuns.length === 0}
                        >
                          <div className="btn-icon" style={{ width: "16px", height: "16px" }}>{iconDownload}</div>
                          <span>Download all SKUs without filters</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="column-visibility-container" ref={forecastColsRef}>
                  <button
                    id="column-toggle-btn"
                    className="toggle-btn column-toggle-btn"
                    onClick={() => setIsColumnDropdownOpen(!isColumnDropdownOpen)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#f5f5f5" viewBox="0 0 256 256">
                      <path d="M84,52V92H28a4,4,0,0,1-4-4V64A16,16,0,0,1,40,48H80A4,4,0,0,1,84,52Zm16,152a4,4,0,0,0,4,4h48a4,4,0,0,0,4-4V164H100ZM24,168v24a16,16,0,0,0,16,16H80a4,4,0,0,0,4-4V164H28A4,4,0,0,0,24,168Zm0-56v32a4,4,0,0,0,4,4H84V108H28A4,4,0,0,0,24,112ZM152,48H104a4,4,0,0,0-4,4V92h56V52A4,4,0,0,0,152,48Zm76,60H172v40h56a4,4,0,0,0,4-4V112A4,4,0,0,0,228,108ZM100,148h56V108H100ZM216,48H176a4,4,0,0,0-4,4V92h56a4,4,0,0,0,4-4V64A16,16,0,0,0,216,48Zm12,116H172v40a4,4,0,0,0,4,4h40a16,16,0,0,0,16-16V168A4,4,0,0,0,228,164Z"></path>
                    </svg>
                    <span>COLUMNS</span>
                  </button>
                  {isColumnDropdownOpen && (
                    <div id="column-dropdown" className="column-dropdown show">
                      {(forecastMode === "demand" ? forecastColumns : winrateColumns)
                        .slice(1)
                        .filter((col) => col !== "ACT QTY")
                        .map((colName) => (
                          <div key={colName} className="column-dropdown-item">
                            <span>{colName}</span>
                            <div
                              className="column-visibility-icon"
                              onClick={() => {
                                if (forecastMode === "demand") {
                                  setColumnVisibility((prev) => ({ ...prev, [colName]: !prev[colName] }));
                                } else {
                                  setWinrateColumnVisibility((prev) => ({ ...prev, [colName]: !prev[colName] }));
                                }
                              }}
                            >
                              {(forecastMode === "demand" ? columnVisibility : winrateColumnVisibility)[colName]
                                ? iconVisible
                                : iconHidden}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {isForecastLoading ? (
              <div style={{ textAlign: "center", padding: "3rem", color: "#8899aa", fontSize: "1.1rem" }}>
                Loading forecast data...
              </div>
            ) : forecastMode === "demand" ? (
              <>
                {sortedForecastData.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "3rem", color: "#8899aa", fontSize: "1.1rem" }}>
                    No forecast data available
                  </div>
                ) : (
                <>
                <div className="main-table-container forecast-table-container">
                  <table className="main-table">
                    <thead>
                      <tr>
                        {forecastColumns.map(
                          (colName) =>
                            columnVisibility[colName] && (
                              <th key={colName} onClick={() => requestForecastSort(colName)}
                                style={{ cursor: "pointer", userSelect: "none" }}>
                                {colName}{getForecastSortIndicator(colName)}
                              </th>
                            ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedForecastData.map((row, i) => (
                        <tr key={i}>
                          {columnVisibility["MONTH"] && <td>{formatPeriod(row.year_week)}</td>}
                          {columnVisibility["ACT QTY"] && <td>{formatNumber(row.actual_qty)}</td>}
                          {columnVisibility["ACT QTY Y-1"] && <td>{formatNumber(row.actual_qty_last_year)}</td>}
                          {columnVisibility["FCST QTY - σ"] && <td className="fcst-minus">{formatNumber(row.fcst_qty_lowerbound)}</td>}
                          {columnVisibility["FCST QTY (avg)"] && <td className="fcst-avg">{formatNumber(row.fcst_qty_avg)}</td>}
                          {columnVisibility["FCST QTY + σ"] && <td className="fcst-plus">{formatNumber(row.fcst_qty_upperbound)}</td>}
                          {columnVisibility["ACT VAL Y-1"] && <td>{formatCurrency(row.actual_val_last_year)}</td>}
                          {columnVisibility["FCST VALUE - σ"] && <td className="fcst-minus">{formatCurrency(row.fcst_val_lowerbound)}</td>}
                          {columnVisibility["FCST VALUE (avg)"] && <td className="fcst-avg">{formatCurrency(row.fcst_val_avg)}</td>}
                          {columnVisibility["FCST VALUE + σ"] && <td className="fcst-plus">{formatCurrency(row.fcst_val_upperbound)}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="forecast-graphs" key="demand-graphs">
                  <div className="plotly-chart"
                    onClick={(e) => handleChartClick(e, "forecast", "qty", "Forecast QTY")}>
                    <Plot
                      data={getForecastChartConfig("qty", filteredForecastData, false, comparisonData).traces}
                      layout={getForecastChartConfig("qty", filteredForecastData, false, comparisonData).layout}
                      config={getForecastChartConfig("qty", filteredForecastData, false, comparisonData).config}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                  <div className="plotly-chart"
                    onClick={(e) => handleChartClick(e, "forecast", "value", "Forecast VALUE (at AVG COST)")}>
                    <Plot
                      data={getForecastChartConfig("value", filteredForecastData, false, comparisonData).traces}
                      layout={getForecastChartConfig("value", filteredForecastData, false, comparisonData).layout}
                      config={getForecastChartConfig("value", filteredForecastData, false, comparisonData).config}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                </div>
                </>
                )}
              </>
            ) : (
              <>
                {sortedWinrateData.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "3rem", color: "#8899aa", fontSize: "1.1rem" }}>
                    No win rate data available
                  </div>
                ) : (
                <>
                <div className="main-table-container forecast-table-container">
                  <table className="main-table">
                    <thead>
                      <tr>
                        {winrateColumns.map(
                          (colName) =>
                            showWrCol(colName) && (
                              <th key={colName} onClick={() => requestForecastSort(colName)}
                                style={{ cursor: "pointer", userSelect: "none" }}>
                                {colName}{getForecastSortIndicator(colName)}
                              </th>
                            ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedWinrateData.map((row, i) => (
                        <tr key={i}>
                          {showWrCol("MONTH") && <td>{formatPeriod(row.year_week)}</td>}
                          {showWrCol("ACT WR Y-1 RAW") && (
                            <td className={isWinrateCompareActive ? "compare-mode-cell" : ""}>{renderWinrateCell(row, "actual_wr_last_year")}</td>
                          )}
                          {showWrCol("FCST WR (avg) RAW") && (
                            <td className={`fcst-avg${isWinrateCompareActive ? " compare-mode-cell" : ""}`}>{renderWinrateCell(row, "fcst_qty_avg")}</td>
                          )}
                          {showWrCol("ACT WR Y-1 SHRINKED") && (
                            <td className={isWinrateCompareActive ? "compare-mode-cell" : ""}>{renderWinrateCell(row, "actual_wr_last_year_shrinked")}</td>
                          )}
                          {showWrCol("FCST WR (avg) SHRINKED") && (
                            <td className={`fcst-avg${isWinrateCompareActive ? " compare-mode-cell" : ""}`}>{renderWinrateCell(row, "fcst_qty_avg_shrinked")}</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="forecast-graphs forecast-graphs--single" key="winrate-graphs">
                  <div className="plotly-chart"
                    onClick={(e) => handleChartClick(e, "winrate", "winrate", "Win Rate Forecast")}>
                    <Plot
                      data={getWinrateChartConfig(filteredWinrateData, false, comparisonData).traces}
                      layout={getWinrateChartConfig(filteredWinrateData, false, comparisonData).layout}
                      config={getWinrateChartConfig(filteredWinrateData, false, comparisonData).config}
                      useResizeHandler={true}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                </div>
                </>
                )}
              </>
            )}
          </section>
        </main>
      </div>

      {popupChart && (
        <div
          id="image-popup"
          className="image-popup-overlay"
          style={{ display: "flex" }}
          onClick={closePopup}
        >
          <span id="popup-close" className="popup-close" onClick={closePopup}>
            &times;
          </span>
          <div
            id="popup-chart-container"
            className="popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const { type, name, title } = popupChart;
              let chartConfig;
              if (type === "kpi")
                chartConfig = getKpiChartConfig(
                  name,
                  kpiHistory,
                  globalConstraints,
                  true,
                  config,
                  unconstrainedKpiHistory,
                );
              else if (type === "pie")
                chartConfig = getPieChartConfig(
                  name,
                  pieChartData,
                  true,
                  config,
                );
              else if (type === "forecast")
                chartConfig = getForecastChartConfig(
                  name,
                  filteredForecastData,
                  true,
                  comparisonData,
                );
              else if (type === "winrate")
                chartConfig = getWinrateChartConfig(
                  filteredWinrateData,
                  true,
                  comparisonData,
                );
              else return null;
              return (
                <Plot
                  data={chartConfig.traces}
                  layout={{
                    ...chartConfig.layout,
                    title: {
                      text: title,
                      font: { size: 20, color: "#333" },
                      x: type === "pie" ? 0.41 : 0.5,
                    },
                  }}
                  config={chartConfig.config}
                  style={{ width: "100%", height: "100%" }}
                />
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
}

export default App;
